/*
 * reeve-main.js
 * Single entry point JS dla theme'u REEVE (fork Horizona).
 * IIFE + modularny REEVE namespace, loaded z `defer`, zero dependencies.
 * Reference: REEVE_Architecture.md §4.
 */

(function () {
  'use strict';

  // ===========================================================================
  // 1. Utility helpers
  // ===========================================================================

  function debounce(fn, wait) {
    let timeoutId;
    return function debounced() {
      const ctx = this;
      const args = arguments;
      clearTimeout(timeoutId);
      timeoutId = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  function throttle(fn, wait) {
    let lastCall = 0;
    let trailingTimeout;
    return function throttled() {
      const now = Date.now();
      const ctx = this;
      const args = arguments;
      const remaining = wait - (now - lastCall);
      if (remaining <= 0) {
        lastCall = now;
        fn.apply(ctx, args);
      } else if (!trailingTimeout) {
        trailingTimeout = setTimeout(function () {
          lastCall = Date.now();
          trailingTimeout = null;
          fn.apply(ctx, args);
        }, remaining);
      }
    };
  }

  async function fetchJSON(url, options) {
    const opts = Object.assign(
      {
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
      },
      options || {}
    );
    const response = await fetch(url, opts);
    if (!response.ok) {
      throw new Error('[REEVE.fetchJSON] ' + response.status + ' ' + response.statusText + ' @ ' + url);
    }
    return response.json();
  }

  /**
   * Format ceny z gram-base (Shopify amount w groszach/centach) wg aktywnej waluty marketu.
   * Używa Intl.NumberFormat + Shopify.currency.active (ustawiane przez Shopify na stronie).
   * NIE hardcoduje "zł" — respektuje Market (PL/CZ/DE/HU/SK/RO).
   */
  function formatCurrency(amountInCents, currencyCode) {
    const amount = Number(amountInCents) / 100;
    const currency =
      currencyCode ||
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active);

    if (!currency) {
      console.warn('[REEVE formatCurrency] No currency code available, returning unformatted value');
      return amount.toFixed(2);
    }

    const locale = (document.documentElement.lang || 'pl-PL').replace('_', '-');
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
      }).format(amount);
    } catch (e) {
      return amount.toFixed(2) + ' ' + currency;
    }
  }

  /**
   * Zwraca Shopify route (np. /cart/add, /search/suggest.json) respektując routing marketu.
   * Fallback na ścieżki domyślne jeśli window.routes nie jest ustawione.
   */
  function getRoute(name) {
    const routes = (window.Shopify && window.Shopify.routes) || window.routes || {};
    const fallback = {
      root_url: '/',
      cart_url: '/cart',
      cart_add_url: '/cart/add',
      cart_change_url: '/cart/change',
      cart_update_url: '/cart/update',
      predictive_search_url: '/search/suggest',
      search_url: '/search',
    };
    return routes[name] || fallback[name] || '/';
  }

  /**
   * Lekki helper i18n — czyta z window.REEVE.translations (ustawiane server-side w theme.liquid
   * przez `<script>window.REEVE = window.REEVE || {}; window.REEVE.translations = {...}</script>`
   * jeśli sekcja potrzebuje konkretnych kluczy po stronie klienta). Fallback: klucz.
   * Preferuj `| t` w Liquidzie — ten helper tylko do dynamicznego JS.
   */
  function t(key, replacements) {
    const store = (window.REEVE && window.REEVE.translations) || {};
    let str = store[key] || key;
    if (replacements && typeof replacements === 'object') {
      Object.keys(replacements).forEach(function (k) {
        str = str.replace('{{ ' + k + ' }}', replacements[k]).replace('{{' + k + '}}', replacements[k]);
      });
    }
    return str;
  }

  /**
   * Escape HTML entities — zabezpiecza dynamiczny tekst wstawiany przez innerHTML.
   * Używane przez deliveryTimer (ETA chipy, progress bar text).
   */
  function escapeHtml(str) {
    var el = document.createElement('span');
    el.textContent = str;
    return el.innerHTML;
  }

  /**
   * polishPluralize — CLDR 3-form Polish plural helper.
   * Mirror Liquid algorytmu z reeve-cart-main.liquid (hotfix #1, lekcja §11.5 #27).
   * Eksportowany przez window.REEVE.utils dla live DOM updates (subtitle count,
   * search results, filter counts, comments etc.).
   *
   * Forms per CLDR:
   *   n == 1                       -> singular ("1 produkt")
   *   n mod 100 in [12..14]        -> many     ("{n} produktow") — teens edge case
   *   n mod 10 in [2..4]           -> few      ("{n} produkty")  — 2,3,4,22,23,24,...
   *   else (0, 5-11, 15-21, 25-31) -> many     ("{n} produktow")
   *
   * Placeholder: {{ count }} w each template string — replaced with actual number.
   *
   * Usage:
   *   polishPluralize(2, '1 produkt', '{{ count }} produkty', '{{ count }} produktow')
   *   → '2 produkty'
   */
  function polishPluralize(count, singular, few, many) {
    if (count === 1) return singular.replace(/\{\{\s*count\s*\}\}/g, count);
    var mod100 = count % 100;
    var mod10 = count % 10;
    if (mod100 >= 12 && mod100 <= 14) return many.replace(/\{\{\s*count\s*\}\}/g, count);
    if (mod10 >= 2 && mod10 <= 4) return few.replace(/\{\{\s*count\s*\}\}/g, count);
    return many.replace(/\{\{\s*count\s*\}\}/g, count);
  }

  // ---------------------------------------------------------------------------
  // 1b. REEVE_ICONS — hardcoded SVG markup for JS-rendered elements.
  //     Source: snippets/reeve-icon.liquid (Lucide). Components §7 §17.
  // ---------------------------------------------------------------------------
  var REEVE_ICONS = {
    check: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>',
    clock: '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>',
    info: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>'
  };

  // ===========================================================================
  // 2. Moduły — każdy self-contained, sprawdza swój root element i wraca jeśli
  //    go nie ma. Selektory po `data-reeve-*`, nigdy po klasach.
  // ===========================================================================

  function initHeader() {
    const root = document.querySelector('[data-reeve-header]');
    if (!root) return;

    // Sticky hide-on-scroll: chowamy header przy scrollu w dół, pokazujemy w górę.
    // Drawer mobile (hamburger) = task 1.2.
    let lastY = window.scrollY;
    const threshold = 80;

    const onScroll = throttle(() => {
      const currentY = window.scrollY;
      const delta = currentY - lastY;

      if (currentY <= threshold) {
        root.removeAttribute('data-reeve-header-hidden');
      } else if (delta > 4) {
        root.setAttribute('data-reeve-header-hidden', 'true');
      } else if (delta < -4) {
        root.removeAttribute('data-reeve-header-hidden');
      }

      lastY = currentY;
    }, 16);

    window.addEventListener('scroll', onScroll, { passive: true });

    // ------ Dropdown Pomoc — hover-only (desktop), keyboard enhancement ------
    // CSS obsługuje otwarcie na :hover i :focus-within. Klik na trigger nie ma
    // efektu (desktop = hover-only). JS dodaje tylko a11y enhancement:
    // Enter/Space/ArrowDown na triggerze → focus 1. linku (CSS :focus-within
    // otwiera dropdown), Arrow Up/Down nawigacja między itemami, Escape →
    // return focus na trigger (focusout z submenu zamyka via CSS).
    const dropdownTriggers = root.querySelectorAll('[data-reeve-header-dropdown-trigger]');
    dropdownTriggers.forEach((trigger) => {
      const dropdown = document.getElementById(trigger.getAttribute('aria-controls'));
      if (!dropdown) return;

      const getLinks = () => Array.from(dropdown.querySelectorAll('a[href]'));

      trigger.addEventListener('click', (e) => {
        e.preventDefault();
      });

      trigger.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
          e.preventDefault();
          const first = getLinks()[0];
          if (first) first.focus();
        }
      });

      dropdown.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          trigger.focus();
          return;
        }
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          const arr = getLinks();
          const idx = arr.indexOf(document.activeElement);
          if (idx === -1) return;
          const next = e.key === 'ArrowDown'
            ? arr[(idx + 1) % arr.length]
            : arr[(idx - 1 + arr.length) % arr.length];
          next.focus();
        }
      });
    });

    // ------ Drawer mobile (task 1.2) ------
    const toggle = root.querySelector('[data-reeve-header-menu-toggle]');
    const drawer = document.querySelector('[data-reeve-drawer]');
    if (!toggle || !drawer) return;

    let lastFocus = null;
    const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

    function getFocusables() {
      return Array.from(drawer.querySelectorAll(focusableSelector)).filter((el) => {
        return el.offsetParent !== null || el === document.activeElement;
      });
    }

    function openDrawer() {
      lastFocus = document.activeElement;
      drawer.setAttribute('aria-hidden', 'false');
      drawer.removeAttribute('inert');
      toggle.setAttribute('aria-expanded', 'true');
      document.body.classList.add('reeve-scroll-lock');
      document.addEventListener('keydown', onKeydown);
      const first = drawer.querySelector('[data-reeve-drawer-close]');
      if (first) first.focus();
    }

    function closeDrawer() {
      drawer.setAttribute('aria-hidden', 'true');
      drawer.setAttribute('inert', '');
      toggle.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('reeve-scroll-lock');
      document.removeEventListener('keydown', onKeydown);
      if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
    }

    function onKeydown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeDrawer();
        return;
      }
      if (e.key !== 'Tab') return;
      const focusables = getFocusables();
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        last.focus();
        e.preventDefault();
      } else if (!e.shiftKey && document.activeElement === last) {
        first.focus();
        e.preventDefault();
      }
    }

    toggle.addEventListener('click', () => {
      const isOpen = drawer.getAttribute('aria-hidden') === 'false';
      if (isOpen) closeDrawer();
      else openDrawer();
    });

    drawer.querySelectorAll('[data-reeve-drawer-close]').forEach((el) => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        closeDrawer();
      });
    });

    // Hotfix 2026-04-20 — close drawer on link click EXCEPT tel:/mailto:
    // (drawer trust block has phone + email anchors; closing mid-OS-handoff
    // breaks dialer/mail-client launch). Delegation survives section.reload.
    drawer.addEventListener('click', (event) => {
      const link = event.target.closest('a[href]');
      if (!link || !drawer.contains(link)) return;
      const href = link.getAttribute('href') || '';
      if (href.startsWith('tel:') || href.startsWith('mailto:')) return;
      closeDrawer();
    });
  }

  function initFooter() {
    const root = document.querySelector('[data-reeve-footer]');
    if (!root) return;

    const cols = root.querySelectorAll('[data-reeve-footer-col]');
    if (!cols.length) return;

    // Desktop (≥1024px): kolumny statyczne, zawsze otwarte.
    // Mobile (<1024px): <details> działa jako akordeon, zamknięte domyślnie.
    // Markup renderuje się z `open` dla progressive enhancement (bez JS wszystko widoczne).
    const mq = window.matchMedia('(min-width: 1024px)');

    function sync() {
      const desktop = mq.matches;
      cols.forEach((el) => {
        el.open = desktop;
      });
    }

    sync();

    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', sync);
    } else if (typeof mq.addListener === 'function') {
      mq.addListener(sync);
    }
  }

  /**
   * Predictive search overlay — task 3.1.
   * Refs: REEVE_Architecture.md §11, REEVE_Design.md §5.5, REEVE_Design_Pages.md §7.3.2.
   *
   * Triggery: [data-reeve-search-trigger="desktop-input|mobile-icon|drawer-input"] w headerze.
   * Fetch: /search/suggest.json z dual query przy PL locale (original + stemmed).
   * PL stemming: normalizePolish (ą→a, …) + stripSuffix (ami/ach/ego/emu/owi/ów/om/em/ej).
   * localStorage: reeve_recent_searches (LIFO, max 5, dedup).
   * A11y: role="dialog", focus trap, Esc, ArrowDown/Up nav, aria-live na wynikach.
   *
   * Bug fix przy okazji: submit desktop form z empty query NIE trafia na /search?q=,
   * tylko otwiera overlay (poprzednio powodowalo "nie znaleziono wynikow dla ''").
   */
  function initPredictiveSearch() {
    const root = document.querySelector('[data-reeve-search-overlay]');
    if (!root) return;

    // === Config z <script type="application/json"> ===
    const cfgNode = root.querySelector('[data-reeve-search-i18n]');
    let cfg = { viewAll: 'See all ({{ count }})', errorFetch: 'Error', currency: 'PLN', locale: 'pl' };
    if (cfgNode) {
      try { cfg = Object.assign(cfg, JSON.parse(cfgNode.textContent)); } catch (e) {
        console.warn('[REEVE.predictiveSearch] cfg JSON parse failed', e);
      }
    }
    const resultsLimit = parseInt(root.dataset.reeveResultsLimit || root.getAttribute('data-reeve-results-limit') || '8', 10);
    const LS_KEY = 'reeve_recent_searches';
    const RECENT_MAX = 5;
    const DEBOUNCE_MS = 200;
    const CLOSE_DELAY_MS = 200; // match --reeve-duration-slow
    const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

    const input = root.querySelector('[data-reeve-search-input]');
    const dialog = root.querySelector('.reeve-search-overlay__dialog');
    const template = root.querySelector('[data-reeve-search-result-template]');
    const loadingEl = root.querySelector('[data-reeve-search-loading]');
    const resultsEl = root.querySelector('[data-reeve-search-results]');
    const emptyInitialEl = root.querySelector('[data-reeve-search-empty-initial]');
    const emptyNoresultsEl = root.querySelector('[data-reeve-search-empty-noresults]');
    const viewAllEl = root.querySelector('[data-reeve-search-view-all]');
    const noresultsQueryEl = root.querySelector('[data-reeve-search-noresults-query]');
    const productsSection = root.querySelector('[data-reeve-search-results-products]');
    const collectionsSection = root.querySelector('[data-reeve-search-results-collections]');
    const pagesSection = root.querySelector('[data-reeve-search-results-pages]');
    const recentSection = root.querySelector('[data-reeve-search-recent]');
    const recentListEl = root.querySelector('[data-reeve-search-recent-list]');

    if (!input || !template) {
      console.warn('[REEVE.predictiveSearch] missing required input/template; abort init');
      return;
    }

    let lastTrigger = null;
    let isOpen = false;
    let focusedResultIdx = -1;
    // Guard: gdy close() refokusuje desktop/drawer-input, ich focus-handlery
    // odpalilyby open() ponownie -> overlay reopens, a zaplanowany setHidden
    // 200ms pozniej chowa overlay zostawiajac scroll-lock na <body>.
    // Skutek: user "zamyka" wyszukiwarke ale strona nie scrolluje.
    let suppressOpenOnFocus = false;
    let hideTimeoutId = null;

    // ========================================================================
    // OPEN / CLOSE
    // ========================================================================
    function open(trigger, prefillValue) {
      if (isOpen) return;
      // Cancel pending hide z poprzedniego close() (defense in depth — gdyby
      // open zostal wywolany w trakcie 200ms transition close).
      if (hideTimeoutId !== null) {
        clearTimeout(hideTimeoutId);
        hideTimeoutId = null;
      }
      // Zamknij drawer mobile jesli otwarty (click na [data-reeve-drawer-close]).
      const openDrawer = document.querySelector('[data-reeve-drawer][aria-hidden="false"]');
      if (openDrawer) {
        const dClose = openDrawer.querySelector('[data-reeve-drawer-close]');
        if (dClose) dClose.click();
      }

      lastTrigger = trigger || document.activeElement;
      root.removeAttribute('hidden');
      // force reflow tak zeby transition zadzialal
      // eslint-disable-next-line no-unused-expressions
      root.offsetHeight;
      requestAnimationFrame(function () {
        root.classList.add('reeve-search-overlay--open');
      });
      document.body.classList.add('reeve-scroll-lock');
      document.addEventListener('keydown', onKeydown, true);
      isOpen = true;

      if (prefillValue && prefillValue.trim().length >= 2) {
        input.value = prefillValue;
        setTimeout(handleInput, 0);
      } else {
        showState('initial');
      }

      // focus input po starcie transition
      setTimeout(function () { input.focus(); input.select(); }, 80);
    }

    function close() {
      if (!isOpen) return;
      root.classList.remove('reeve-search-overlay--open');
      document.body.classList.remove('reeve-scroll-lock');
      document.removeEventListener('keydown', onKeydown, true);
      isOpen = false;
      hideTimeoutId = setTimeout(function () {
        root.setAttribute('hidden', '');
        hideTimeoutId = null;
      }, CLOSE_DELAY_MS);
      if (lastTrigger && typeof lastTrigger.focus === 'function') {
        // Focus event jest synchronniczny — flag musi byc set->focus->reset
        // w jednym tick zeby zlapac focus-handler na desktop/drawer-input.
        suppressOpenOnFocus = true;
        try { lastTrigger.focus(); } catch (e) { /* noop */ }
        suppressOpenOnFocus = false;
      }
    }

    function onKeydown(e) {
      if (e.key === 'Escape') { e.preventDefault(); close(); return; }
      if (e.key === 'Tab') { trapFocus(e); return; }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        moveResultFocus(e.key === 'ArrowDown' ? 1 : -1);
        e.preventDefault();
      }
    }

    function trapFocus(e) {
      const focusables = Array.from(dialog.querySelectorAll(FOCUSABLE))
        .filter(function (el) { return !el.hidden && el.offsetParent !== null; });
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus();
      }
    }

    function moveResultFocus(direction) {
      const links = Array.from(root.querySelectorAll('.reeve-search-overlay__result-link'));
      if (!links.length) return;
      focusedResultIdx = Math.max(0, Math.min(links.length - 1, focusedResultIdx + direction));
      // jesli przed pierwszym i strzalka w gore — focus wraca na input
      if (focusedResultIdx === 0 && direction === -1 && document.activeElement === links[0]) {
        input.focus(); focusedResultIdx = -1; return;
      }
      const target = links[focusedResultIdx];
      if (target) { target.setAttribute('tabindex', '0'); target.focus(); }
    }

    // ========================================================================
    // TRIGGERS + SUBMIT BUG FIX
    // ========================================================================
    function wireTriggers() {
      document.querySelectorAll('[data-reeve-search-trigger="desktop-input"]').forEach(function (el) {
        el.addEventListener('focus', function (e) {
          if (suppressOpenOnFocus) return;
          e.preventDefault(); el.blur();
          open(el, el.value);
        });
        el.addEventListener('click', function (e) {
          e.preventDefault(); open(el, el.value);
        });
      });
      document.querySelectorAll('[data-reeve-search-trigger="mobile-icon"]').forEach(function (el) {
        el.addEventListener('click', function (e) {
          e.preventDefault(); open(el);
        });
      });
      document.querySelectorAll('[data-reeve-search-trigger="drawer-input"]').forEach(function (el) {
        el.addEventListener('focus', function (e) {
          if (suppressOpenOnFocus) return;
          e.preventDefault(); el.blur();
          open(el, el.value);
        });
        el.addEventListener('click', function (e) {
          e.preventDefault(); open(el, el.value);
        });
      });

      // Bug fix: submit desktop form z empty/<2-char query -> overlay zamiast /search?q=
      document.querySelectorAll('[data-reeve-header-search-form]').forEach(function (form) {
        form.addEventListener('submit', function (e) {
          const qInput = form.querySelector('input[name="q"]');
          const val = qInput ? qInput.value.trim() : '';
          if (val.length < 2) {
            e.preventDefault();
            open(form, val);
          } else {
            pushRecent(val);
          }
        });
      });
    }

    // ========================================================================
    // CLOSE / POPULAR / RECENT EVENT BINDINGS
    // ========================================================================
    root.querySelectorAll('[data-reeve-search-overlay-close]').forEach(function (el) {
      el.addEventListener('click', function () { close(); });
    });
    root.querySelectorAll('[data-reeve-search-popular-term]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        input.value = btn.getAttribute('data-reeve-search-popular-term');
        input.focus();
        handleInput();
      });
    });

    // ========================================================================
    // INPUT HANDLING (debounced)
    // ========================================================================
    const debouncedHandle = debounce(handleInput, DEBOUNCE_MS);
    input.addEventListener('input', debouncedHandle);
    input.addEventListener('keydown', function (e) {
      // Enter w input z >=2 znakami -> pelna strona wynikow + push recent
      if (e.key === 'Enter') {
        const val = input.value.trim();
        if (val.length >= 2) {
          pushRecent(val);
          window.location.href = getRoute('search_url') + '?q=' + encodeURIComponent(val);
          e.preventDefault();
        } else {
          e.preventDefault();
        }
      }
    });

    function handleInput() {
      const q = input.value.trim();
      if (q.length < 2) { showState('initial'); return; }
      showState('loading');
      fetchSuggestions(q)
        .then(function (results) {
          if (results.totalCount === 0) { showNoResults(q); }
          else { renderResults(results, q); }
        })
        .catch(function (err) {
          console.warn('[REEVE.predictiveSearch] fetch error', err);
          const toast = (window.REEVE && window.REEVE.utils && window.REEVE.utils.showToast);
          if (toast) toast(cfg.errorFetch, 'error', 4000);
          showState('initial');
        });
    }

    // ========================================================================
    // PL STEMMING
    // ========================================================================
    function normalizePolish(s) {
      return s.toLowerCase()
        .replace(/\u0105/g, 'a').replace(/\u0107/g, 'c').replace(/\u0119/g, 'e')
        .replace(/\u0142/g, 'l').replace(/\u0144/g, 'n').replace(/\u00F3/g, 'o')
        .replace(/\u015B/g, 's').replace(/\u017A/g, 'z').replace(/\u017C/g, 'z');
    }
    // Koncowki najdluzsze pierwsze tak zeby match byl greedy.
    const SUFFIXES = ['owi', 'ego', 'emu', 'ami', 'ach', '\u00F3w', 'om', 'em', 'ej'];
    function stripSuffix(s) {
      if (s.length <= 5) return s;
      for (let i = 0; i < SUFFIXES.length; i++) {
        const sfx = SUFFIXES[i];
        if (s.endsWith(sfx) && s.length - sfx.length >= 3) {
          return s.slice(0, -sfx.length);
        }
      }
      return s;
    }
    function buildQueries(q) {
      const isPl = (cfg.locale || '').toLowerCase().indexOf('pl') === 0
        || (document.documentElement.lang || '').toLowerCase().indexOf('pl') === 0;
      if (!isPl) return [q];
      const stemmed = stripSuffix(normalizePolish(q));
      return stemmed === q.toLowerCase() ? [q] : [q, stemmed];
    }

    // ========================================================================
    // FETCH + MERGE
    // ========================================================================
    function fetchSuggestions(q) {
      const queries = buildQueries(q);
      const baseUrl = '/search/suggest.json';
      const url = function (qs) {
        return baseUrl + '?q=' + encodeURIComponent(qs)
          + '&resources[type]=product,collection,page'
          + '&resources[limit]=' + resultsLimit
          + '&resources[options][unavailable_products]=last';
      };
      return Promise.all(queries.map(function (qs) { return fetchJSON(url(qs)); }))
        .then(mergeResults);
    }

    function mergeResults(responses) {
      const seen = { products: new Set(), collections: new Set(), pages: new Set() };
      const out = { products: [], collections: [], pages: [], totalCount: 0 };
      responses.forEach(function (r) {
        const res = (r && r.resources && r.resources.results) || {};
        (res.products || []).forEach(function (p) {
          if (!seen.products.has(p.id)) { seen.products.add(p.id); out.products.push(p); }
        });
        (res.collections || []).forEach(function (c) {
          if (!seen.collections.has(c.id)) { seen.collections.add(c.id); out.collections.push(c); }
        });
        (res.pages || []).forEach(function (p) {
          if (!seen.pages.has(p.id)) { seen.pages.add(p.id); out.pages.push(p); }
        });
      });
      out.products = out.products.slice(0, resultsLimit);
      out.collections = out.collections.slice(0, 4);
      out.pages = out.pages.slice(0, 3);
      out.totalCount = out.products.length + out.collections.length + out.pages.length;
      return out;
    }

    // ========================================================================
    // RENDER — products / collections / pages
    // ========================================================================
    function renderResults(results, originalQ) {
      const productsList = productsSection ? productsSection.querySelector('ul') : null;
      const collectionsList = collectionsSection ? collectionsSection.querySelector('ul') : null;
      const pagesList = pagesSection ? pagesSection.querySelector('ul') : null;

      if (productsList) {
        productsList.innerHTML = '';
        results.products.forEach(function (p) { productsList.appendChild(buildProductItem(p)); });
        toggleHidden(productsSection, results.products.length === 0);
      }
      if (collectionsList) {
        collectionsList.innerHTML = '';
        results.collections.forEach(function (c) { collectionsList.appendChild(buildSimpleItem(c)); });
        toggleHidden(collectionsSection, results.collections.length === 0);
      }
      if (pagesList) {
        pagesList.innerHTML = '';
        results.pages.forEach(function (p) { pagesList.appendChild(buildSimpleItem(p)); });
        toggleHidden(pagesSection, results.pages.length === 0);
      }

      if (viewAllEl) {
        viewAllEl.href = getRoute('search_url') + '?q=' + encodeURIComponent(originalQ);
        viewAllEl.textContent = String(cfg.viewAll).replace('__COUNT__', results.totalCount);
      }

      focusedResultIdx = -1;
      showState('results');
    }

    function buildProductItem(product) {
      const li = template.content.firstElementChild.cloneNode(true);
      const link = li.querySelector('[data-ref="link"]');
      const img = li.querySelector('[data-ref="image"]');
      const titleEl = li.querySelector('[data-ref="title"]');
      const priceEl = li.querySelector('[data-ref="price"]');
      const badgeEl = li.querySelector('[data-ref="badge"]');

      link.href = product.url;
      titleEl.textContent = product.title || '';

      if (img) {
        if (product.image) {
          img.src = product.image;
          img.alt = product.title || '';
        } else {
          img.remove();
        }
      }

      if (priceEl) {
        // /search/suggest.json zwraca price jako string dziesietny ("55.00"),
        // NIE grosze (inaczej niz /cart.js). Parsuj jako float, mnoz *100
        // zeby przekazac grosze do formatCurrency (ktory dzieli przez 100).
        const raw = (product.price != null ? product.price : product.price_min) || 0;
        const asFloat = (typeof raw === 'string') ? parseFloat(raw.replace(',', '.')) : Number(raw);
        const priceCents = Math.round((isNaN(asFloat) ? 0 : asFloat) * 100);
        priceEl.textContent = formatCurrency(priceCents, cfg.currency);
      }

      // Badge — fallback: jesli product.available === false -> "Wyprzedane" (translate via t()).
      // Tag-based sprawdzanie ("Ostatnie sztuki") wymaga product.tags — suggest.json NIE zwraca tags,
      // wiec opieramy sie na product.available.
      if (badgeEl) {
        if (product.available === false) {
          badgeEl.textContent = cfg.soldOut || 'Wyprzedane';
          badgeEl.hidden = false;
        } else {
          badgeEl.remove();
        }
      }

      return li;
    }

    function buildSimpleItem(obj) {
      const li = document.createElement('li');
      const a = document.createElement('a');
      a.href = obj.url;
      a.className = 'reeve-search-overlay__result-link reeve-search-overlay__result-link--simple';
      a.textContent = obj.title || '';
      a.tabIndex = -1;
      li.appendChild(a);
      return li;
    }

    // ========================================================================
    // NO RESULTS + STATE SWITCHER
    // ========================================================================
    function showNoResults(q) {
      if (noresultsQueryEl) {
        noresultsQueryEl.textContent = '\u201E' + q + '\u201D';
      }
      showState('noresults');
    }

    function showState(name) {
      toggleHidden(loadingEl, name !== 'loading');
      toggleHidden(resultsEl, name !== 'results');
      toggleHidden(emptyInitialEl, name !== 'initial');
      toggleHidden(emptyNoresultsEl, name !== 'noresults');
      if (name === 'initial') { renderRecent(); }
    }

    function toggleHidden(el, shouldHide) {
      if (!el) return;
      if (shouldHide) el.setAttribute('hidden', '');
      else el.removeAttribute('hidden');
    }

    // ========================================================================
    // LOCALSTORAGE RECENT SEARCHES
    // ========================================================================
    function loadRecent() {
      try { return JSON.parse(localStorage.getItem(LS_KEY) || '[]'); }
      catch (e) { return []; }
    }
    function saveRecent(list) {
      try { localStorage.setItem(LS_KEY, JSON.stringify(list.slice(0, RECENT_MAX))); }
      catch (e) { /* storage quota / disabled — noop */ }
    }
    function pushRecent(q) {
      if (!q || q.length < 2) return;
      const list = loadRecent().filter(function (x) { return x !== q; });
      list.unshift(q);
      saveRecent(list);
    }
    function removeRecent(q) {
      saveRecent(loadRecent().filter(function (x) { return x !== q; }));
      renderRecent();
    }
    function clearRecent() {
      try { localStorage.removeItem(LS_KEY); } catch (e) { /* noop */ }
      renderRecent();
    }
    function renderRecent() {
      if (!recentSection || !recentListEl) return;
      const list = loadRecent();
      if (!list.length) { recentSection.setAttribute('hidden', ''); return; }
      recentSection.removeAttribute('hidden');
      recentListEl.innerHTML = '';
      list.forEach(function (q) {
        const li = document.createElement('li');
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'reeve-search-overlay__chip reeve-search-overlay__chip--recent';
        chip.textContent = q;
        chip.addEventListener('click', function () {
          input.value = q;
          input.focus();
          handleInput();
        });
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'reeve-search-overlay__chip-remove';
        const removeAria = (cfg.recentRemoveAria || 'Remove: __QUERY__').replace('__QUERY__', q);
        remove.setAttribute('aria-label', removeAria);
        remove.textContent = '\u00D7';
        remove.addEventListener('click', function (e) {
          e.stopPropagation();
          removeRecent(q);
        });
        li.appendChild(chip);
        li.appendChild(remove);
        recentListEl.appendChild(li);
      });
    }

    const clearBtn = root.querySelector('[data-reeve-search-recent-clear]');
    if (clearBtn) clearBtn.addEventListener('click', clearRecent);

    // ========================================================================
    // Public API (pozwala innym modulom otworzyc overlay programowo)
    // ========================================================================
    window.REEVE = Object.assign(window.REEVE || {}, {
      predictiveSearch: { open: open, close: close }
    });

    // ========================================================================
    // INIT
    // ========================================================================
    wireTriggers();
    renderRecent();
    showState('initial');
  }

  /**
   * Product gallery — swipe mobile (native scroll-snap), thumbs desktop (click swap),
   * counter sync via IntersectionObserver, lightbox HD via modules.modal.
   * Refs: Design_Pages §4.2, Components §16 (modal), Images §4 (widths).
   */
  function initProductGallery() {
    const root = document.querySelector('[data-reeve-pdp-gallery]');
    if (!root) return;

    const mainScroller = root.querySelector('[data-reeve-pdp-gallery-main]');
    const slides = Array.from(root.querySelectorAll('[data-reeve-pdp-gallery-item]'));
    const thumbs = Array.from(root.querySelectorAll('[data-reeve-pdp-gallery-thumb]'));
    const counterCurrent = root.querySelector('[data-reeve-pdp-gallery-current]');
    const prevBtn = root.querySelector('[data-reeve-pdp-gallery-prev]');
    const nextBtn = root.querySelector('[data-reeve-pdp-gallery-next]');
    const zoomTriggers = root.querySelectorAll('[data-reeve-pdp-gallery-zoom]');

    const total = slides.length;
    if (total === 0) return;

    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const mq = window.matchMedia('(min-width: 1024px)');
    let activeIndex = 0;
    let observerActive = false;

    // ---- Core: go to slide ----
    function goTo(index, options) {
      const opts = options || {};
      index = ((index % total) + total) % total;
      if (index === activeIndex && !opts.force) return;

      // Pause departing video
      const departingVideo = slides[activeIndex].querySelector('video');
      if (departingVideo) departingVideo.pause();

      // Desktop: crossfade via class toggle
      if (mq.matches) {
        slides[activeIndex].classList.remove('reeve-pdp-gallery__main-item--active');
        slides[index].classList.add('reeve-pdp-gallery__main-item--active');
      } else if (!opts.fromObserver) {
        // Mobile: scroll to slide (skip when observer triggered the change)
        mainScroller.scrollTo({
          left: slides[index].offsetLeft,
          behavior: prefersReduced ? 'auto' : 'smooth'
        });
      }

      // Update thumbs
      thumbs[activeIndex].classList.remove('reeve-pdp-gallery__thumb--active');
      thumbs[activeIndex].removeAttribute('aria-current');
      thumbs[index].classList.add('reeve-pdp-gallery__thumb--active');
      thumbs[index].setAttribute('aria-current', 'true');
      thumbs[index].scrollIntoView({
        block: 'nearest',
        inline: 'nearest',
        behavior: prefersReduced ? 'auto' : 'smooth'
      });

      // Update counter
      if (counterCurrent) counterCurrent.textContent = index + 1;

      // Video autoplay desktop
      if (mq.matches) {
        const arrivingVideo = slides[index].querySelector('video[data-reeve-video-autoplay-desktop]');
        if (arrivingVideo) arrivingVideo.play().catch(function () { /* autoplay blocked — silent */ });
      }

      activeIndex = index;
    }

    // ---- Thumb clicks ----
    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        goTo(parseInt(thumb.getAttribute('data-media-index'), 10));
      });
    });

    // ---- Nav arrows (desktop) ----
    if (prevBtn) prevBtn.addEventListener('click', function () { goTo(activeIndex - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { goTo(activeIndex + 1); });

    // ---- Mobile: IntersectionObserver for scroll-snap sync ----
    function setupMobileObserver() {
      if (observerActive || !mainScroller) return;
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            var idx = parseInt(entry.target.getAttribute('data-media-index'), 10);
            if (idx !== activeIndex) goTo(idx, { fromObserver: true });
          }
        });
      }, { root: mainScroller, threshold: 0.5 });
      slides.forEach(function (slide) { io.observe(slide); });
      observerActive = true;
    }

    if (!mq.matches) setupMobileObserver();
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', function () {
        if (!mq.matches) setupMobileObserver();
        // On resize to desktop, ensure active slide is visible
        if (mq.matches) goTo(activeIndex, { force: true });
      });
    }

    // ---- Lightbox ----
    var lightboxId = 'reeve-pdp-gallery-lightbox';
    var lightboxSlides = Array.from(root.querySelectorAll('[data-reeve-pdp-gallery-lightbox-item]'));
    var lightboxCurrent = root.querySelector('[data-reeve-pdp-gallery-lightbox-current]');
    var lightboxPrev = root.querySelector('[data-reeve-pdp-gallery-lightbox-prev]');
    var lightboxNext = root.querySelector('[data-reeve-pdp-gallery-lightbox-next]');
    var lightboxSlidesContainer = root.querySelector('[data-reeve-pdp-gallery-lightbox-slides]');
    var lightboxIndex = 0;

    function lightboxGoTo(index) {
      index = ((index % total) + total) % total;

      // Desktop: crossfade
      if (mq.matches) {
        lightboxSlides[lightboxIndex].classList.remove('reeve-pdp-gallery__lightbox-item--active');
        lightboxSlides[index].classList.add('reeve-pdp-gallery__lightbox-item--active');
      } else if (lightboxSlidesContainer) {
        // Mobile: scroll
        lightboxSlidesContainer.scrollTo({
          left: lightboxSlides[index].offsetLeft,
          behavior: prefersReduced ? 'auto' : 'smooth'
        });
      }

      // Pause departing video in lightbox
      var departVid = lightboxSlides[lightboxIndex].querySelector('video');
      if (departVid) departVid.pause();

      if (lightboxCurrent) lightboxCurrent.textContent = index + 1;
      lightboxIndex = index;
    }

    function onLightboxKey(e) {
      if (e.key === 'ArrowLeft') { e.preventDefault(); lightboxGoTo(lightboxIndex - 1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); lightboxGoTo(lightboxIndex + 1); }
    }

    // Open lightbox
    function openLightbox() {
      lightboxGoTo(activeIndex);
      if (window.REEVE && window.REEVE._modalOpen) {
        window.REEVE._modalOpen(lightboxId);
      }
      document.addEventListener('keydown', onLightboxKey);
    }

    // Wire zoom triggers
    zoomTriggers.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        openLightbox();
      });
    });

    // Lightbox nav
    if (lightboxPrev) lightboxPrev.addEventListener('click', function () { lightboxGoTo(lightboxIndex - 1); });
    if (lightboxNext) lightboxNext.addEventListener('click', function () { lightboxGoTo(lightboxIndex + 1); });

    // Clean up lightbox key listener when modal closes
    var lightboxEl = document.getElementById(lightboxId);
    if (lightboxEl) {
      var observer = new MutationObserver(function (mutations) {
        mutations.forEach(function (m) {
          if (m.attributeName === 'hidden' && lightboxEl.hasAttribute('hidden')) {
            document.removeEventListener('keydown', onLightboxKey);
            // Pause all lightbox videos on close
            lightboxSlides.forEach(function (s) {
              var v = s.querySelector('video');
              if (v) v.pause();
            });
          }
        });
      });
      observer.observe(lightboxEl, { attributes: true });
    }
  }

  /**
   * Variant picker — option chip UI + Section Rendering API selective DOM update.
   * Refs: task 2.13, Design_Pages §4.1 (variants), Architecture §4.
   *
   * Strategy (plan decision #0.6): selective DOM update (no flicker, stepper state preserved):
   *   1. User clicks chip → update aria-pressed + option value label
   *   2. Find matching variant via REEVE_PRODUCT_VARIANTS + REEVE_PRODUCT_OPTIONS_MAP
   *   3. Update hidden variant input, URL (history.replaceState), optionally fetch section
   *      for price/stock/CTA refresh
   *
   * Public API on window.REEVE.modules.variantPicker:
   *   hasSelection() — bool
   *
   * Customer Events:
   *   window reeve:variant:change — { variantId, variant, productId }
   */
  function initVariantPicker() {
    var picker = document.querySelector('[data-reeve-variant-picker]');
    if (!picker) return;

    var buyboxRoot = picker.closest('[data-reeve-pdp-buybox]');
    if (!buyboxRoot) return;

    var form = document.getElementById('reeve-buybox-form');
    var variantInput = form ? form.querySelector('input[name="id"]') : null;
    var dataScript = buyboxRoot.querySelector('[data-reeve-buybox-product-data]');
    var productData = {};
    try {
      if (dataScript) productData = JSON.parse(dataScript.textContent);
    } catch (e) { /* swallowed */ }

    var variants = window.REEVE_PRODUCT_VARIANTS || [];
    var optionsMap = window.REEVE_PRODUCT_OPTIONS_MAP || {};

    function currentSelections() {
      var selections = {};
      var groups = picker.querySelectorAll('[data-reeve-option-index]');
      groups.forEach(function (group) {
        var active = group.querySelector('[data-reeve-option-chip][aria-pressed="true"]');
        if (active) {
          selections[active.getAttribute('data-reeve-option-name')] =
            active.getAttribute('data-reeve-option-value');
        }
      });
      return selections;
    }

    function findMatchingVariant(selections) {
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        var matchesAll = true;
        var keys = Object.keys(selections);
        for (var k = 0; k < keys.length; k++) {
          var optName = keys[k];
          var idx = optionsMap[optName];
          if (!idx) { matchesAll = false; break; }
          if (v['option' + idx] !== selections[optName]) { matchesAll = false; break; }
        }
        if (matchesAll) return v;
      }
      return null;
    }

    function applyVariant(variant) {
      if (!variant) return;
      if (variantInput) variantInput.value = variant.id;

      // Update URL without reload
      try {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      } catch (e) { /* pre-IE11-friendly swallow */ }

      // Selective DOM update via Section Rendering API
      var handle = productData.product_handle;
      var sectionId = productData.section_id;
      if (!handle || !sectionId) return;

      var fetchUrl = '/products/' + handle + '?variant=' + variant.id + '&section_id=' + sectionId;
      fetch(fetchUrl, { headers: { 'Accept': 'text/html' } })
        .then(function (r) {
          if (!r.ok) throw new Error('[REEVE.variantPicker] HTTP ' + r.status);
          return r.text();
        })
        .then(function (html) {
          if (!html) return;
          var parser = new DOMParser();
          var doc = parser.parseFromString(html, 'text/html');

          // Price
          var newPrice = doc.querySelector('[data-reeve-price-wrap]');
          var oldPrice = buyboxRoot.querySelector('[data-reeve-price-wrap]');
          if (newPrice && oldPrice) oldPrice.innerHTML = newPrice.innerHTML;

          // Stock chip
          var newStock = doc.querySelector('[data-reeve-stock-indicator]');
          var oldStock = buyboxRoot.querySelector('[data-reeve-stock-indicator]');
          if (newStock && oldStock) oldStock.innerHTML = newStock.innerHTML;

          // Submit CTA state + text
          var newSubmit = doc.querySelector('[data-reeve-buybox-submit]');
          var oldSubmit = buyboxRoot.querySelector('[data-reeve-buybox-submit]');
          if (newSubmit && oldSubmit) {
            oldSubmit.disabled = newSubmit.disabled;
            var newText = newSubmit.querySelector('[data-reeve-submit-text]');
            var oldText = oldSubmit.querySelector('[data-reeve-submit-text]');
            if (newText && oldText) oldText.textContent = newText.textContent;
          }
        })
        .catch(function (err) {
          console.warn('[REEVE.variantPicker] section fetch failed', err);
        });

      window.dispatchEvent(new CustomEvent('reeve:variant:change', {
        detail: { variantId: variant.id, variant: variant, productId: productData.product_id }
      }));
    }

    function handleChipClick(e) {
      var chip = e.currentTarget;
      var optName = chip.getAttribute('data-reeve-option-name');
      var optValue = chip.getAttribute('data-reeve-option-value');

      // Toggle aria-pressed within this option group only
      var group = chip.closest('[data-reeve-option-index]');
      if (group) {
        group.querySelectorAll('[data-reeve-option-chip]').forEach(function (c) {
          c.setAttribute('aria-pressed', 'false');
          c.classList.remove('reeve-pdp-buybox__option-chip--selected');
        });
      }
      chip.setAttribute('aria-pressed', 'true');
      chip.classList.add('reeve-pdp-buybox__option-chip--selected');

      // Update option value label
      var labelEl = buyboxRoot.querySelector('[data-reeve-selected-value-for="' + optName + '"]');
      if (labelEl) labelEl.textContent = optValue;

      var selections = currentSelections();
      var matched = findMatchingVariant(selections);

      if (!matched) {
        // Unusual combo — mark CTA as variant_unavailable
        var submitBtn = buyboxRoot.querySelector('[data-reeve-buybox-submit]');
        var submitText = submitBtn ? submitBtn.querySelector('[data-reeve-submit-text]') : null;
        if (submitBtn) submitBtn.disabled = true;
        if (submitText) submitText.textContent = t('reeve.sections.reeve_pdp_buybox.cta.variant_unavailable');
        return;
      }
      applyVariant(matched);
    }

    picker.querySelectorAll('[data-reeve-option-chip]').forEach(function (chip) {
      chip.addEventListener('click', handleChipClick);
    });

    function hasSelection() {
      return !!(variantInput && variantInput.value && variantInput.value !== 'null');
    }

    window.REEVE = window.REEVE || {};
    window.REEVE.modules = window.REEVE.modules || {};
    window.REEVE.modules.variantPicker = Object.assign(window.REEVE.modules.variantPicker || {}, {
      hasSelection: hasSelection
    });
  }

  /**
   * PDP buybox — form submit via AJAX, stepper, Customer Events dispatch.
   * Refs: task 2.13, Design_Pages §4.1, Architecture §4, SEO_Analytics §5, CLAUDE.md §11.5 #15.
   *
   * Public API exported on window.REEVE.modules.buybox:
   *   addToCart()               — async, programmatic add (used by sticky CTA)
   *   hasSelectedVariant()      — bool (always true for single-variant products)
   *   getCurrentVariantId()     — string|null
   *   getProductData()          — { product_id, product_handle, product_title, featured_image, selected_variant_id, available, section_id }
   *
   * Customer Events dispatched:
   *   window   reeve:product:view  (on init)
   *   window   reeve:cart:update   (after successful add; detail.cart, detail.item)
   *   document cart:change         (dual-scope per §11.5 #15 — for modules.deliveryTimer)
   */
  function initBuybox() {
    var root = document.querySelector('[data-reeve-pdp-buybox]');
    if (!root) return;

    var form = root.querySelector('#reeve-buybox-form');
    var submitBtn = root.querySelector('[data-reeve-buybox-submit]');
    var submitText = submitBtn ? submitBtn.querySelector('[data-reeve-submit-text]') : null;
    var stepperInput = root.querySelector('[data-reeve-stepper-input]');
    var stepperDec = root.querySelector('[data-reeve-stepper-decrement]');
    var stepperInc = root.querySelector('[data-reeve-stepper-increment]');
    var dataScript = root.querySelector('[data-reeve-buybox-product-data]');

    var productData = {};
    if (dataScript) {
      try {
        productData = JSON.parse(dataScript.textContent);
      } catch (e) {
        console.warn('[REEVE.buybox] product data parse failed', e);
      }
    }

    // Dispatch product:view on init (Customer Event for analytics 4.5)
    window.dispatchEvent(new CustomEvent('reeve:product:view', {
      detail: {
        productId: productData.product_id,
        handle: productData.product_handle,
        variantId: productData.selected_variant_id
      }
    }));

    // --- Stepper ---
    if (stepperInput && stepperDec) {
      stepperDec.addEventListener('click', function () {
        var current = parseInt(stepperInput.value, 10) || 1;
        var min = parseInt(stepperInput.min, 10) || 1;
        if (current > min) stepperInput.value = current - 1;
      });
    }
    if (stepperInput && stepperInc) {
      stepperInc.addEventListener('click', function () {
        var current = parseInt(stepperInput.value, 10) || 1;
        var max = parseInt(stepperInput.max, 10) || 99;
        if (current < max) stepperInput.value = current + 1;
      });
    }
    if (stepperInput) {
      stepperInput.addEventListener('change', function () {
        var v = parseInt(stepperInput.value, 10);
        var min = parseInt(stepperInput.min, 10) || 1;
        var max = parseInt(stepperInput.max, 10) || 99;
        if (isNaN(v) || v < min) stepperInput.value = min;
        else if (v > max) stepperInput.value = max;
      });
    }

    // --- Public addToCart (used by form submit AND sticky CTA) ---
    function addToCart() {
      if (!form) return Promise.resolve({ success: false, error: 'no_form' });
      if (submitBtn && submitBtn.disabled) {
        return Promise.resolve({ success: false, error: 'disabled' });
      }

      var originalText = submitText ? submitText.textContent : '';
      if (submitBtn) submitBtn.disabled = true;
      if (submitText) {
        submitText.textContent = t('reeve.sections.reeve_pdp_buybox.cta.adding');
      }

      var formData = new FormData(form);

      return fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Accept': 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      })
        .then(function (resp) {
          if (!resp.ok) {
            return resp.json().then(function (err) {
              throw new Error(err.description || err.message || ('HTTP ' + resp.status));
            }).catch(function () {
              throw new Error('HTTP ' + resp.status);
            });
          }
          return resp.json();
        })
        .then(function (addedItem) {
          // Fetch fresh cart state for counter + drawer sync
          return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
            .then(function (r) { return r.json(); })
            .then(function (cartData) {
              // Dual-scope dispatch (CLAUDE.md §11.5 #15)
              window.dispatchEvent(new CustomEvent('reeve:cart:update', {
                detail: { action: 'add', item: addedItem, cart: cartData }
              }));
              document.dispatchEvent(new CustomEvent('cart:change', {
                detail: { cart: cartData }
              }));

              if (window.REEVE && window.REEVE.utils && window.REEVE.utils.showToast) {
                window.REEVE.utils.showToast(
                  t('reeve.sections.reeve_pdp_buybox.toast.added'),
                  'success'
                );
              }

              if (submitText) submitText.textContent = originalText;
              if (submitBtn) submitBtn.disabled = false;

              return { success: true, item: addedItem, cart: cartData };
            });
        })
        .catch(function (err) {
          console.warn('[REEVE.buybox] addToCart failed', err);
          if (submitText) submitText.textContent = originalText;
          if (submitBtn) submitBtn.disabled = false;

          var msg = err && err.message ? err.message : '';
          var isSoldOut = /sold out|out of stock|unavailable|wyprzedan/i.test(msg);
          var toastKey = isSoldOut
            ? 'reeve.sections.reeve_pdp_buybox.toast.error_sold_out'
            : 'reeve.sections.reeve_pdp_buybox.toast.error_generic';
          if (window.REEVE && window.REEVE.utils && window.REEVE.utils.showToast) {
            window.REEVE.utils.showToast(t(toastKey), 'error', 4000);
          }
          return { success: false, error: msg };
        });
    }

    // Form submit — prevent native, use AJAX addToCart
    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        addToCart();
      });
    }

    function hasSelectedVariant() {
      // Single-variant product — always true
      var picker = root.querySelector('[data-reeve-variant-picker]');
      if (!picker) return true;
      // Check that current variant ID in form matches a valid variant
      var input = form ? form.querySelector('input[name="id"]') : null;
      return !!(input && input.value && input.value !== 'null');
    }

    function getCurrentVariantId() {
      var input = form ? form.querySelector('input[name="id"]') : null;
      return input ? input.value : null;
    }

    function getProductData() {
      return productData;
    }

    // Export public API (Object.assign — CLAUDE.md §11.5 #10)
    window.REEVE = window.REEVE || {};
    window.REEVE.modules = window.REEVE.modules || {};
    window.REEVE.modules.buybox = Object.assign(window.REEVE.modules.buybox || {}, {
      addToCart: addToCart,
      hasSelectedVariant: hasSelectedVariant,
      getCurrentVariantId: getCurrentVariantId,
      getProductData: getProductData
    });
  }

  /**
   * Cart MINIMAL — header counter sync. Pełny cart page = task 2.20.
   * Refs: task 2.13, Design_Pages §4.1 (add feedback).
   *
   * Listens on both scopes (CLAUDE.md §11.5 #15):
   *   window   reeve:cart:update  (REEVE event, dispatched by modules.buybox)
   *   document cart:change        (Horizon-compatible)
   */
  /**
   * PDP sticky CTA — mobile-only bar + buybox delegation.
   * Refs: task 2.19.2, Design_Pages §4.11.
   *
   * Show logic: IntersectionObserver na main CTA i footer; visible gdy main CTA
   * niewidoczne AND footer niewidoczne AND viewport mobile (<1024px).
   * Click: delegat do window.REEVE.modules.buybox.addToCart().
   */
  function initPdpStickyCta() {
    var stickyBar = document.querySelector('[data-reeve-pdp-sticky-cta]');
    if (!stickyBar) return;

    var mainCta = document.querySelector('[data-reeve-buybox-submit]');
    var footer = document.querySelector('.reeve-footer, [data-reeve-footer]') || document.querySelector('footer');
    var stickyBtn = stickyBar.querySelector('[data-reeve-sticky-add]');
    var btnText = stickyBar.querySelector('[data-reeve-sticky-btn-text]');
    if (!mainCta || !stickyBtn || !btnText) return;

    var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var mobileBp = 1024;
    // Hotfix ATC feedback (2026-04-22) — Design_Pages §4.11.1.
    // Po ATC button staje sie "Zobacz koszyk" linkiem w Signal fill przez 4s,
    // klik w tym stanie nawiguje do /cart zamiast triggerowac ATC.
    var AFTER_ATC_CLASS = 'reeve-pdp-sticky-cta__button--after-atc';

    var mainCtaVisible = true;
    var footerVisible = false;
    var isAdding = false;
    var lastAddedAt = 0;

    function isMobile() { return window.innerWidth < mobileBp; }

    function updateVisibility() {
      if (!isMobile()) {
        stickyBar.classList.remove('reeve-pdp-sticky-cta--visible');
        stickyBar.setAttribute('aria-hidden', 'true');
        return;
      }
      var shouldShow = !mainCtaVisible && !footerVisible;
      stickyBar.classList.toggle('reeve-pdp-sticky-cta--visible', shouldShow);
      stickyBar.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
    }

    var ctaObserver = new IntersectionObserver(function (entries) {
      mainCtaVisible = entries[0].isIntersecting;
      updateVisibility();
    }, { threshold: 0 });
    ctaObserver.observe(mainCta);

    if (footer) {
      var footerObserver = new IntersectionObserver(function (entries) {
        footerVisible = entries[0].isIntersecting;
        updateVisibility();
      }, { threshold: 0 });
      footerObserver.observe(footer);
    }

    window.addEventListener('resize', throttle(updateVisibility, 200));

    stickyBtn.addEventListener('click', function (e) {
      e.preventDefault();

      // Hotfix ATC feedback (2026-04-22): w stanie after-ATC button jest linkiem
      // do koszyka, nie triggerem ATC. Design_Pages §4.11.1.
      if (stickyBtn.classList.contains(AFTER_ATC_CLASS)) {
        window.location.assign('/cart');
        return;
      }

      if (isAdding) return;

      // Check variant selection (multi-variant products only)
      var picker = document.querySelector('[data-reeve-variant-picker]');
      if (picker && window.REEVE && window.REEVE.modules && window.REEVE.modules.variantPicker) {
        if (typeof window.REEVE.modules.variantPicker.hasSelection === 'function' &&
            !window.REEVE.modules.variantPicker.hasSelection()) {
          mainCta.scrollIntoView({
            behavior: prefersReducedMotion ? 'auto' : 'smooth',
            block: 'center'
          });
          if (window.REEVE.utils && window.REEVE.utils.showToast) {
            window.REEVE.utils.showToast(t('reeve.sections.reeve_pdp_sticky_cta.select_variant'), 'info', 2500);
          }
          return;
        }
      }

      if (!window.REEVE || !window.REEVE.modules || !window.REEVE.modules.buybox ||
          typeof window.REEVE.modules.buybox.addToCart !== 'function') {
        console.warn('[REEVE.pdpStickyCta] buybox.addToCart not available');
        return;
      }

      isAdding = true;
      stickyBtn.disabled = true;

      Promise.resolve(window.REEVE.modules.buybox.addToCart())
        .then(function (result) {
          if (result && result.success) {
            // Hotfix ATC feedback (2026-04-22): re-enable od razu zeby CSS [disabled]
            // gray override nie trzymal przycisku wyszarzonego przez 4s. Dodajemy
            // modyfikator --after-atc ktory odslania ikone arrow-right i gwarantuje
            // ze Signal fill z .reeve-btn--primary jest widoczny.
            stickyBtn.disabled = false;
            stickyBtn.classList.add(AFTER_ATC_CLASS);
            var originalText = btnText.textContent;
            btnText.textContent = t('reeve.sections.reeve_pdp_sticky_cta.view_cart');
            lastAddedAt = Date.now();
            var addedAt = lastAddedAt;
            setTimeout(function () {
              // Only restore if no new add happened in the meantime
              if (addedAt === lastAddedAt) {
                stickyBtn.classList.remove(AFTER_ATC_CLASS);
                btnText.textContent = originalText;
                isAdding = false;
              }
            }, 4000);
          } else {
            stickyBtn.disabled = false;
            isAdding = false;
          }
        })
        .catch(function () {
          stickyBtn.disabled = false;
          isAdding = false;
        });
    });
  }

  /**
   * Task 3.4 — modules.recentlyViewed
   * Refs: REEVE_BuildPlan.md §3.4, Design_Pages §1.9.5 + §4.13, Architecture §4.
   *
   * Storage: localStorage key `reeve_recently_viewed`, array of handles, max 8, LIFO.
   * Dodawanie: PDP trigger przez buybox.getProductData() (sync-safe, uniknie race
   * z event dispatch during buybox init). Defensive listener na `reeve:product:view`
   * dla late dispatches (variant picker route change, AJAX PDP).
   * Render: fetch /products/<handle>?view=reeve-card-tile (alt-view, `{% layout none %}`)
   * -> innerHTML inject. Wymog min 2 kart post-filter + post-404. Max 4 render.
   * Safari Private Mode: try/catch na setItem + init, sekcja zostaje `hidden`.
   */
  function initRecentlyViewed() {
    var LS_KEY = 'reeve_recently_viewed';
    var MAX = 8;
    var SHOW = 4;
    // Handle Shopify realny: lowercase alfanumeryczne + myslniki, min 1 znak.
    // Polskie znaki sa transliterowane przy generowaniu handle, wiec regex jest bezpieczny.
    var HANDLE_RE = /^[a-z0-9][a-z0-9-]*$/;

    function isValidHandle(h) {
      return typeof h === 'string' && HANDLE_RE.test(h);
    }

    // Warstwa 1: sanityzacja + self-heal. Zatruty wpis (np. "") w localStorage
    // powodowal fetch /products/?view=... ktory Shopify serwuje jako PELNA
    // strone listy produktow (HTTP 200) -> innerHTML wstrzykiwal cala strone
    // do kafelka. Wpis nie znikal sam (addHandle deduplikuje tylko biezacy
    // handle), wiec klienci z zatrutym storage zostawali z bugiem na zawsze
    // bez nadpisania listy tutaj.
    function getList() {
      try {
        var raw = localStorage.getItem(LS_KEY);
        var parsed = raw ? JSON.parse(raw) : [];
        if (!Array.isArray(parsed)) return [];
        var clean = parsed.filter(isValidHandle);
        if (clean.length !== parsed.length) {
          try {
            localStorage.setItem(LS_KEY, JSON.stringify(clean));
          } catch (e) {
            // Safari Private Mode / QuotaExceeded — silent
          }
        }
        return clean;
      } catch (e) {
        return [];
      }
    }

    function addHandle(handle) {
      if (!isValidHandle(handle)) return;
      try {
        var list = getList().filter(function (h) { return h !== handle; });
        list.unshift(handle);
        localStorage.setItem(LS_KEY, JSON.stringify(list.slice(0, MAX)));
      } catch (e) {
        // Safari Private Mode / QuotaExceeded — silent
      }
    }

    // Store current PDP handle synchronously via buybox public API (avoids
    // event-race: modules init in registry order; listener added AFTER buybox
    // init would miss the synchronous dispatch during buybox init).
    try {
      if (
        window.REEVE &&
        window.REEVE.modules &&
        window.REEVE.modules.buybox &&
        typeof window.REEVE.modules.buybox.getProductData === 'function'
      ) {
        var pd = window.REEVE.modules.buybox.getProductData();
        if (pd && pd.product_handle) addHandle(pd.product_handle);
      }
    } catch (e) {
      // ignore
    }

    // Defensive late-dispatch listener — variant picker route change / AJAX PDP.
    window.addEventListener('reeve:product:view', function (evt) {
      if (evt && evt.detail && evt.detail.handle) addHandle(evt.detail.handle);
    });

    var root = document.querySelector('[data-reeve-recently-viewed]');
    if (!root) return;
    var track = root.querySelector('[data-reeve-recently-viewed-track]');
    if (!track) return;

    try {
      var handles = getList();
      if (root.dataset.reeveExcludeCurrent === 'true') {
        var current = root.dataset.reeveCurrentProductHandle;
        if (current) {
          handles = handles.filter(function (h) { return h !== current; });
        }
      }
      handles = handles.slice(0, SHOW);
      if (handles.length < 2) return; // section stays hidden

      Promise.all(handles.map(function (h) {
        // Warstwa 2: guard przed fetchem — pas bezpieczenstwa gdyby cos
        // ominelo warstwe 1 (np. handle dodany miedzy getList() a fetch).
        if (!isValidHandle(h)) return null;
        return fetch('/products/' + encodeURIComponent(h) + '?view=reeve-card-tile', {
          headers: { 'Accept': 'text/html' }
        })
          .then(function (r) { return r.ok ? r.text() : null; })
          .then(function (text) {
            if (typeof text !== 'string') return null;
            // Warstwa 3: walidacja odpowiedzi przed injekcja. Alt-view
            // `{% layout none %}` renderuje DOKLADNIE jeden
            // reeve-product-card <article>. Kazda inna odpowiedz (pelna
            // strona listy produktow, redirect, przyszla zmiana platformy)
            // jest odrzucana jak 404.
            var trimmed = text.trimStart ? text.trimStart() : text.replace(/^\s+/, '');
            var looksLikeTile = trimmed.indexOf('<article') === 0 &&
              text.indexOf('data-reeve-product-card') !== -1;
            return looksLikeTile ? text : null;
          })
          .catch(function () { return null; });
      })).then(function (htmls) {
        var valid = htmls.filter(function (html) {
          return typeof html === 'string' && html.trim().length > 0;
        });
        if (valid.length < 2) return; // post-404 still <2 -> stays hidden
        track.innerHTML = valid.join('');
        root.removeAttribute('hidden');
      }).catch(function (e) {
        console.warn('[REEVE.recentlyViewed]', e);
      });
    } catch (e) {
      console.warn('[REEVE.recentlyViewed]', e);
    }
  }

  /**
   * Task 2.20 — modules.cart rozszerzone z minimal (counter only) do pelnego cart page.
   *
   * Counter sync: dziala na kazdej stronie (listen reeve:cart:update + cart:change).
   * Cart page features: za guardem [data-reeve-cart-main] (tylko na /cart).
   *
   * Public API (Object.assign, lekcja #10):
   *   window.REEVE.modules.cart.updateQuantity(lineKey, quantity)
   *   window.REEVE.modules.cart.removeItem(lineKey, productId, quantity, title)
   *   window.REEVE.modules.cart.getCartState()
   *
   * Customer Events (dual scope, lekcja #15):
   *   reeve:cart:view        — on DOMContentLoaded gdy /cart (window)
   *   reeve:cart:update      — na add/remove/stepper (window + document)
   *   reeve:checkout:start   — na main CTA click przed navigation (window)
   *
   * Discount code — hybrid pattern (per Krok 0 runtime test):
   *   1. POST /cart/update.js z {discount: CODE} → sprawdz discount_codes[0].applicable
   *   2. Jesli applicable:true → stash sessionStorage['reeve:cart:pending_discount'] = CODE
   *      + success message + append ?discount=CODE na main CTA href
   *   3. Jesli false → toast error
   */
  function initCart() {
    // Hotfix ATC feedback (2026-04-22): header renderuje DWA badge'y cart count
    // (mobile L98 + desktop row1 L189 w sections/reeve-header.liquid). Wczesniej
    // querySelector zwracal tylko pierwszy (mobile, hidden na desktop) → desktop
    // licznik nie aktualizowal sie po ATC. Iteracja przez NodeList = oba syncuja sie.
    var counters = document.querySelectorAll('[data-reeve-cart-count]');

    function updateCount(count) {
      if (!counters.length) return;
      var label = t('reeve.cart.aria_label_with_count') || 'Cart: {{ count }}';
      var ariaLabel = label.replace('{{ count }}', count);
      counters.forEach(function (counter) {
        counter.textContent = count;
        counter.setAttribute('aria-label', ariaLabel);
        if (count > 0) {
          counter.removeAttribute('hidden');
          counter.classList.add('reeve-cart-count--visible');
        } else {
          counter.setAttribute('hidden', '');
          counter.classList.remove('reeve-cart-count--visible');
        }
      });
    }

    function handleCartEvent(e) {
      if (e && e.detail && e.detail.cart && typeof e.detail.cart.item_count !== 'undefined') {
        updateCount(e.detail.cart.item_count);
      }
    }

    window.addEventListener('reeve:cart:update', handleCartEvent);
    document.addEventListener('cart:change', handleCartEvent);

    // --- Cart page features (only on /cart) ---
    var cartMain = document.querySelector('[data-reeve-cart-main]');

    // Shared cart state cache (latest fetched via /cart.js or from events)
    var cartState = null;

    function cacheCart(cart) {
      cartState = cart;
    }

    function fetchCart() {
      return fetch('/cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (c) { cacheCart(c); return c; });
    }

    function getCartState() {
      return cartState;
    }

    function dispatchCartUpdate(cart, extraDetail) {
      cacheCart(cart);
      var detail = Object.assign({ cart: cart }, extraDetail || {});
      window.dispatchEvent(new CustomEvent('reeve:cart:update', { detail: detail }));
      document.dispatchEvent(new CustomEvent('cart:change', { detail: detail }));
    }

    // Shared helper — zwraca showToast jesli dostepny, inaczej noop
    function toast(message, type, duration, options) {
      if (window.REEVE && window.REEVE.utils && typeof window.REEVE.utils.showToast === 'function') {
        return window.REEVE.utils.showToast(message, type, duration, options);
      }
    }

    /**
     * Hotfix cart polish Bug 3 — SHARED HELPER: delivery cards sync on cart total change.
     *
     * Problem: reeve-delivery-card renderuje is_free server-side Liquid z cart.total_price
     * w momencie page load. Gdy cart total zmienia sie (add/remove/stepper) — Liquid state
     * jest zamrozony, karty nie updatuja -> klient widzi ceny mimo ze progress bar pokazuje
     * "Masz darmowa dostawe" (bug report: cart 2.20 post-merge walkthrough).
     *
     * Strategia: shared JS helper updatujacy DOM bezposrednio na reeve:cart:update event.
     * Wywolywany z: (a) modules.cart listener (automatyczny, wewnetrzny), (b) public API
     * window.REEVE.modules.cart.updateDeliveryCards(cartTotal) dla analitycznych integracji.
     *
     * Aplikuje sie do kazdej strony gdzie sa [data-reeve-delivery-card]:
     *   - /cart — reeve-cart-main (task 2.20)
     *   - /products/X — reeve-pdp-delivery (task 2.14) — dubluje z deliveryTimer logic,
     *     ale idempotent toggle (class + innerHTML) nie powoduje flicker.
     *
     * Data attrs (set przez reeve-delivery-card snippet):
     *   - data-reeve-delivery-card="paczkomat|courier"
     *   - data-reeve-delivery-free-threshold="9999" (cents)
     *   - data-reeve-delivery-price-raw="1299" (cents, method-specific)
     */
    function updateDeliveryCards(cartTotal) {
      var cards = document.querySelectorAll('[data-reeve-delivery-card]');
      if (!cards.length) return;
      var freeText = t('reeve.shipping.free_chip') || 'GRATIS';

      cards.forEach(function (card) {
        var threshold = parseInt(card.getAttribute('data-reeve-delivery-free-threshold'), 10) || 0;
        var priceRaw = parseInt(card.getAttribute('data-reeve-delivery-price-raw'), 10) || 0;
        var isFree = threshold > 0 && cartTotal >= threshold;

        card.classList.toggle('reeve-delivery-card--free-shipping', isFree);

        var priceEl = card.querySelector('[data-reeve-delivery-price]');
        if (!priceEl) return;

        if (isFree) {
          // Re-render chip HTML (matches reeve-chip snippet output: icon check + span text)
          priceEl.innerHTML =
            '<span class="reeve-chip reeve-chip--success">' +
              '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false" class="reeve-chip__icon">' +
                '<path d="M20 6 9 17l-5-5"/>' +
              '</svg>' +
              '<span>' + escapeHtml(freeText) + '</span>' +
            '</span>';
        } else {
          var priceFormatted = window.REEVE.utils.formatCurrency(priceRaw);
          priceEl.innerHTML = '<span class="reeve-delivery-card__price-amount">' + escapeHtml(priceFormatted) + '</span>';
        }
      });
    }

    // Listen reeve:cart:update globally — helper runs na cart page AND PDP (buybox add-to-cart).
    window.addEventListener('reeve:cart:update', function (e) {
      if (!e.detail || !e.detail.cart) return;
      updateDeliveryCards(e.detail.cart.total_price);
    });

    // Expose public API wczesnie — inne moduly (cartStickyBar) moga wolac getCartState
    window.REEVE = window.REEVE || {};
    window.REEVE.modules = window.REEVE.modules || {};
    window.REEVE.modules.cart = Object.assign(window.REEVE.modules.cart || {}, {
      updateQuantity: function (lineKey, quantity) { return updateQuantity(lineKey, quantity); },
      removeItem: function (lineKey, productId, quantity, title) { return removeItem(lineKey, productId, quantity, title); },
      getCartState: getCartState,
      updateDeliveryCards: updateDeliveryCards,
    });

    if (!cartMain) {
      return; // Counter sync only — no cart page
    }

    // Fetch fresh cart state on load (cache for accelerated flow etc.)
    fetchCart().catch(function () { /* silent — summary uses Liquid values */ });

    // Dispatch reeve:cart:view (window) for analytics
    window.dispatchEvent(new CustomEvent('reeve:cart:view', {
      detail: { timestamp: Date.now() },
    }));

    var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // --- Stepper: quantity change with debounce 500ms ---
    function attachStepperHandlers(stepperRoot) {
      var input = stepperRoot.querySelector('[data-reeve-cart-stepper-input]');
      var btnDec = stepperRoot.querySelector('[data-reeve-cart-stepper-dec]');
      var btnInc = stepperRoot.querySelector('[data-reeve-cart-stepper-inc]');
      var line = stepperRoot.closest('[data-reeve-cart-line]');
      if (!input || !btnDec || !btnInc || !line) return;

      var lineKey = line.getAttribute('data-line-key');
      var min = parseInt(input.min, 10) || 1;
      var max = parseInt(input.max, 10) || 99;

      var debounced = debounce(function (qty) {
        updateQuantity(lineKey, qty).catch(function () { /* error already toasted */ });
      }, 500);

      function setQty(newQty, immediate) {
        if (newQty < 0) newQty = 0;
        if (newQty > max) {
          toast((t('reeve.sections.reeve_cart_main.toasts.stock_exceeded') || 'Only {{ max }} in stock').replace('{{ max }}', max), 'error', 3000);
          newQty = max;
        }
        input.value = newQty;
        if (immediate) {
          updateQuantity(lineKey, newQty).catch(function () { /* error already toasted */ });
        } else {
          debounced(newQty);
        }
      }

      btnDec.addEventListener('click', function () {
        var v = parseInt(input.value, 10) || min;
        // v == 1 → removeItem (treat as trash click)
        if (v <= 1) {
          var removeBtn = line.querySelector('[data-reeve-cart-remove]');
          if (removeBtn) removeBtn.click();
          return;
        }
        setQty(v - 1, false);
      });

      btnInc.addEventListener('click', function () {
        var v = parseInt(input.value, 10) || min;
        setQty(v + 1, false);
      });

      input.addEventListener('change', function () {
        var v = parseInt(input.value, 10);
        if (isNaN(v) || v < min) v = min;
        if (v === 0) {
          var removeBtn2 = line.querySelector('[data-reeve-cart-remove]');
          if (removeBtn2) removeBtn2.click();
          return;
        }
        setQty(v, true);
      });
    }

    function updateQuantity(lineKey, quantity) {
      return fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: quantity }),
      })
        .then(function (r) {
          if (!r.ok) return r.json().then(function (err) { throw err; });
          return r.json();
        })
        .then(function (cart) {
          updateLineUi(cart, lineKey);
          dispatchCartUpdate(cart);
          return cart;
        })
        .catch(function (err) {
          var errMsg = (err && err.message) || t('reeve.sections.reeve_cart_main.toasts.update_error') || 'Failed to update cart';
          toast(errMsg, 'error', 3000);
          throw err;
        });
    }

    function updateLineUi(cart, changedLineKey) {
      // Refresh line totals + summary from fresh cart
      var lines = cartMain.querySelectorAll('[data-reeve-cart-line]');
      lines.forEach(function (lineEl) {
        var key = lineEl.getAttribute('data-line-key');
        var item = cart.items.find(function (it) { return it.key === key; });
        if (!item) return;
        var lineTotal = lineEl.querySelector('[data-reeve-cart-line-total]');
        if (lineTotal) lineTotal.textContent = window.REEVE.utils.formatCurrency(item.final_line_price);
      });
      var subtotal = cartMain.querySelector('[data-reeve-cart-subtotal]');
      if (subtotal) subtotal.textContent = window.REEVE.utils.formatCurrency(cart.items_subtotal_price);
      var total = cartMain.querySelector('[data-reeve-cart-total]');
      if (total) total.textContent = window.REEVE.utils.formatCurrency(cart.total_price);
      var savedLine = cartMain.querySelector('[data-reeve-cart-saved-line]');
      var saved = cartMain.querySelector('[data-reeve-cart-saved]');
      if (cart.total_discount > 0) {
        if (saved) saved.textContent = '-' + window.REEVE.utils.formatCurrency(cart.total_discount);
        if (savedLine) savedLine.style.display = '';
      } else if (savedLine) {
        savedLine.style.display = 'none';
      }

      // Shipping line: always visible, update text + --free modifier. Stan B ukrywa
      // adnotację "Cena bez kosztów dostawy" pod Razem (nie ukrywa linii Dostawa).
      var threshold = 0;
      var progressEl = document.querySelector('[data-reeve-progress]');
      if (progressEl) threshold = parseInt(progressEl.getAttribute('data-reeve-threshold'), 10) || 0;
      var hasFreeShipping = threshold > 0 && cart.total_price >= threshold;

      var shippingValue = cartMain.querySelector('[data-reeve-cart-shipping-value]');
      if (shippingValue) {
        var textFree = shippingValue.dataset.textFree || '';
        var textTbd = shippingValue.dataset.textTbd || '';
        shippingValue.textContent = hasFreeShipping ? textFree : textTbd;
        shippingValue.classList.toggle('reeve-cart-summary__shipping-value--free', hasFreeShipping);
      }
      var totalShippingNote = cartMain.querySelector('[data-reeve-cart-total-shipping-note]');
      if (totalShippingNote) totalShippingNote.toggleAttribute('hidden', hasFreeShipping);

      // Hotfix cart polish #2 Bug 2 (BLOCKER) — subtitle count live sync.
      // Liquid renderuje count_label server-side (CLDR 3-form), ale po cart:update
      // event potrzebna JS pluralizacja. Reuse window.REEVE.utils.polishPluralize.
      var subtitleEl = cartMain.querySelector('[data-reeve-cart-count-label]');
      if (subtitleEl && window.REEVE && window.REEVE.utils && typeof window.REEVE.utils.polishPluralize === 'function') {
        var singularTpl = t('reeve.sections.reeve_cart_main.count_singular');
        var fewTpl = t('reeve.sections.reeve_cart_main.count_few');
        var manyTpl = t('reeve.sections.reeve_cart_main.count_many');
        subtitleEl.textContent = window.REEVE.utils.polishPluralize(cart.item_count, singularTpl, fewTpl, manyTpl);
      }
    }

    // --- Remove with optimistic fade-out + undo toast ---
    function removeItem(lineKey, productId, quantity, title) {
      var line = cartMain.querySelector('[data-reeve-cart-line][data-line-key="' + lineKey + '"]');
      if (line) line.classList.add('reeve-cart-line--removing');

      return fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: 0 }),
      })
        .then(function (r) {
          if (!r.ok) return r.json().then(function (err) { throw err; });
          return r.json();
        })
        .then(function (cart) {
          var removeTimeout = setTimeout(function () {
            if (line && line.parentNode) line.parentNode.removeChild(line);
            if (cart.item_count === 0) {
              window.location.reload();
              return;
            }
            updateLineUi(cart, lineKey);
            dispatchCartUpdate(cart);
          }, prefersReducedMotion ? 0 : 260);

          // Toast z Cofnij (5s window)
          var undoMsg = t('reeve.sections.reeve_cart_main.toasts.removed') || 'Removed from cart';
          var undoLabel = t('reeve.sections.reeve_cart_main.toasts.undo') || 'Undo';
          toast(undoMsg, 'info', 5000, {
            action: {
              label: undoLabel,
              callback: function () {
                clearTimeout(removeTimeout);
                if (line) line.classList.remove('reeve-cart-line--removing');
                // Re-add z poprzednim qty
                reAddItem(productId, quantity);
              },
            },
          });

          return cart;
        })
        .catch(function (err) {
          if (line) line.classList.remove('reeve-cart-line--removing');
          var errMsg = (err && err.message) || t('reeve.sections.reeve_cart_main.toasts.update_error') || 'Failed';
          toast(errMsg, 'error', 3000);
          throw err;
        });
    }

    function reAddItem(productId, quantity) {
      // /cart/add.js with id=variantId preferred — but we only have productId on DOM;
      // Shopify /cart/add.js accepts either variant id or product handle. Use productId
      // as id param (works if product has single default variant; fallback: window.location.reload())
      return fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: productId, quantity: quantity }),
      })
        .then(function (r) { return r.json(); })
        .then(function () {
          window.location.reload();
        })
        .catch(function () {
          window.location.reload();
        });
    }

    // Wire stepper + remove handlers
    cartMain.querySelectorAll('[data-reeve-cart-stepper]').forEach(attachStepperHandlers);
    cartMain.querySelectorAll('[data-reeve-cart-remove]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var line = btn.closest('[data-reeve-cart-line]');
        if (!line) return;
        var lineKey = line.getAttribute('data-line-key');
        var productId = btn.getAttribute('data-product-id');
        var qty = parseInt(btn.getAttribute('data-quantity'), 10) || 1;
        var title = btn.getAttribute('data-title') || '';
        removeItem(lineKey, productId, qty, title);
      });
    });

    // --- Progress bar updater on cart:update ---
    var progressBarEl = document.querySelector('[data-reeve-progress]');
    if (progressBarEl) {
      var threshold = parseInt(progressBarEl.getAttribute('data-reeve-threshold'), 10) || 0;
      var progressFill = progressBarEl.querySelector('.reeve-progress-bar__fill');
      var progressText = progressBarEl.querySelector('[data-reeve-progress-text]');

      function updateProgressBar(cart) {
        if (!threshold || !progressFill) return;
        var pct = Math.min(100, (cart.total_price / threshold) * 100);
        progressFill.style.width = pct + '%';
        progressFill.setAttribute('aria-valuenow', Math.round(pct));
        if (!progressText) return;
        var remaining = threshold - cart.total_price;
        if (remaining <= 0) {
          var reachedText = t('reeve.shipping.free_threshold_reached') || 'Free shipping reached';
          progressText.textContent = reachedText;
        } else {
          var belowTpl = t('reeve.shipping.free_threshold_below_html') || 'Do darmowej dostawy: {{ amount }}';
          var amount = window.REEVE.utils.formatCurrency(remaining);
          // belowTpl moze zawierac HTML (linkacki stary snippet HTML ale to plain text do progress)
          progressText.textContent = belowTpl.replace(/\{\{\s*amount\s*\}\}/g, amount).replace(/<[^>]+>/g, '');
        }
      }

      window.addEventListener('reeve:cart:update', function (e) {
        if (e.detail && e.detail.cart) updateProgressBar(e.detail.cart);
      });
    }

    // --- Discount code hybrid ---
    var discountForm = cartMain.querySelector('[data-reeve-cart-discount-form]');
    var discountInput = cartMain.querySelector('[data-reeve-cart-discount-input]');
    var discountMsg = cartMain.querySelector('[data-reeve-cart-discount-message]');
    var PENDING_DISCOUNT_KEY = 'reeve:cart:pending_discount';
    var TERMS_ACCEPTED_KEY = 'reeve:cart:terms_accepted';

    function appendDiscountToCheckout() {
      var cta = cartMain.querySelector('[data-reeve-checkout-cta]');
      if (!cta) return;
      var code = sessionStorage.getItem(PENDING_DISCOUNT_KEY);
      if (!code) return;
      var href = cta.getAttribute('href') || '/checkout';
      if (href.indexOf('discount=') === -1) {
        var sep = href.indexOf('?') === -1 ? '?' : '&';
        cta.setAttribute('href', href + sep + 'discount=' + encodeURIComponent(code));
      }
    }

    if (discountForm && discountInput) {
      // Restore pending discount from previous session (display only)
      var savedCode = sessionStorage.getItem(PENDING_DISCOUNT_KEY);
      if (savedCode) {
        discountInput.value = savedCode;
        if (discountMsg) {
          discountMsg.textContent = (t('reeve.sections.reeve_cart_main.discount.applied') || 'Code {{ code }} applied').replace('{{ code }}', savedCode)
            + ' ' + (t('reeve.sections.reeve_cart_main.discount.pending_note') || '');
          discountMsg.className = 'reeve-cart-discount__message reeve-cart-discount__message--success';
        }
        appendDiscountToCheckout();
      }

      discountForm.addEventListener('submit', function (e) {
        e.preventDefault();
        var code = (discountInput.value || '').trim();
        if (!code) return;

        fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ discount: code }),
        })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            var codes = cart.discount_codes || [];
            var match = codes[codes.length - 1];
            if (match && match.applicable) {
              sessionStorage.setItem(PENDING_DISCOUNT_KEY, code);
              if (discountMsg) {
                discountMsg.textContent = (t('reeve.sections.reeve_cart_main.discount.applied') || 'Code {{ code }} applied').replace('{{ code }}', code)
                  + ' ' + (t('reeve.sections.reeve_cart_main.discount.pending_note') || '');
                discountMsg.className = 'reeve-cart-discount__message reeve-cart-discount__message--success';
              }
              appendDiscountToCheckout();
            } else {
              sessionStorage.removeItem(PENDING_DISCOUNT_KEY);
              if (discountMsg) {
                discountMsg.textContent = t('reeve.sections.reeve_cart_main.discount.error') || 'Invalid code';
                discountMsg.className = 'reeve-cart-discount__message reeve-cart-discount__message--error';
              }
            }
            dispatchCartUpdate(cart);
          })
          .catch(function () {
            if (discountMsg) {
              discountMsg.textContent = t('reeve.sections.reeve_cart_main.discount.error') || 'Invalid code';
              discountMsg.className = 'reeve-cart-discount__message reeve-cart-discount__message--error';
            }
          });
      });
    }

    // --- Checkbox regulamin validation + sessionStorage persist ---
    var termsCheckbox = cartMain.querySelector('[data-reeve-terms-checkbox]');
    var checkoutCta = cartMain.querySelector('[data-reeve-checkout-cta]');

    function setCtaEnabled(enabled) {
      if (!checkoutCta) return;
      if (enabled) {
        checkoutCta.setAttribute('aria-disabled', 'false');
        checkoutCta.removeAttribute('tabindex');
      } else {
        checkoutCta.setAttribute('aria-disabled', 'true');
        checkoutCta.setAttribute('tabindex', '-1');
      }
    }

    if (termsCheckbox) {
      // Restore from sessionStorage
      if (sessionStorage.getItem(TERMS_ACCEPTED_KEY) === 'true') {
        termsCheckbox.checked = true;
        setCtaEnabled(true);
      }

      termsCheckbox.addEventListener('change', function () {
        if (termsCheckbox.checked) {
          sessionStorage.setItem(TERMS_ACCEPTED_KEY, 'true');
          setCtaEnabled(true);
        } else {
          sessionStorage.removeItem(TERMS_ACCEPTED_KEY);
          setCtaEnabled(false);
        }
      });
    }

    // --- Main CTA — click handler: dispatch reeve:checkout:start, or show toast if disabled ---
    if (checkoutCta) {
      checkoutCta.addEventListener('click', function (e) {
        if (checkoutCta.getAttribute('aria-disabled') === 'true') {
          e.preventDefault();
          var msg = t('reeve.sections.reeve_cart_main.toasts.terms_required')
            || t('reeve.form_errors.terms_required')
            || 'You must accept the terms';
          toast(msg, 'error', 3000);
          if (termsCheckbox) {
            termsCheckbox.focus();
            termsCheckbox.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'center' });
          }
          return;
        }
        // Dispatch checkout:start before navigation
        window.dispatchEvent(new CustomEvent('reeve:checkout:start', {
          detail: { cart: cartState, timestamp: Date.now() },
        }));
      });
    }
  }

  /**
   * Task 2.20 — modules.cartUpsell: lightweight guard.
   * Gdy sekcja reeve-cart-upsell renderuje <2 kart (fallback chain not productive),
   * usuwa wrapper aby nie pokazywac pustego headera "Najczesciej razem".
   */
  function initCartUpsell() {
    var root = document.querySelector('[data-reeve-cart-upsell]');
    if (!root) return;
    var cards = root.querySelectorAll('[data-reeve-rail-cards] .reeve-product-card');
    if (cards.length < 2) {
      root.remove();
    }
  }

  /**
   * Task 2.20 — modules.cartStickyBar (mobile-only sticky bottom checkout).
   *
   * Hotfix cart polish #2 Bug 3: IntersectionObserver → scroll-driven geometry
   * + HYSTERESIS anti-flicker pattern.
   *
   * Root cause flicker: 2 IntersectionObservers (summary + footer, threshold=0)
   * firowaly multiple razy przy micro-scroll na boundary → shouldShow oscylowal
   * → class toggle flicker. Pattern identyczny do PDP sticky CTA (working) ale
   * geometria cart (long upsell + trust section miedzy summary a footer z lazy
   * images) powodowala layout shifts re-triggering observer callbacks.
   *
   * Fix: manualny scroll listener (throttle 80ms) + measure getBoundingClientRect
   * + hysteresis offsets — show threshold 30px past summary-bottom, hide threshold
   * 10px past — gap 20px eliminates micro-scroll jitter state changes.
   */
  function initCartStickyBar() {
    var stickyBar = document.querySelector('[data-reeve-cart-sticky-bar]');
    if (!stickyBar) return;

    var summary = document.querySelector('[data-reeve-cart-summary]');
    var footer = document.querySelector('.reeve-footer, [data-reeve-footer]') || document.querySelector('footer');
    var stickyCta = stickyBar.querySelector('[data-reeve-cart-sticky-cta]');
    var stickyTotal = stickyBar.querySelector('[data-reeve-cart-sticky-total]');
    if (!summary || !stickyCta) return;

    var mobileBp = 1024;
    var showOffset = 30; // px past summary-bottom to SHOW sticky
    var hideOffset = 10; // px past summary-bottom to HIDE sticky (hysteresis gap: 20px)

    var summaryBottomY = 0;
    var footerTopY = Infinity;
    var isShowing = false;

    function isMobile() { return window.innerWidth < mobileBp; }

    function measureTargets() {
      var sumRect = summary.getBoundingClientRect();
      summaryBottomY = sumRect.bottom + window.scrollY;
      if (footer) {
        var footRect = footer.getBoundingClientRect();
        footerTopY = footRect.top + window.scrollY;
      }
    }

    function updateVisibility() {
      if (!isMobile()) {
        if (isShowing) {
          isShowing = false;
          stickyBar.classList.remove('reeve-cart-sticky-bar--visible');
          stickyBar.setAttribute('aria-hidden', 'true');
        }
        return;
      }
      var viewportBottomY = window.scrollY + window.innerHeight;
      // Hysteresis: show/hide thresholds differ → no flicker on micro-scroll
      var threshold = isShowing ? summaryBottomY + hideOffset : summaryBottomY + showOffset;
      var pastSummary = viewportBottomY > threshold;
      // Hide sticky gdy viewport bottom wchodzi w footer area
      var inFooter = viewportBottomY > footerTopY + 100;
      var shouldShow = pastSummary && !inFooter;
      if (shouldShow !== isShowing) {
        isShowing = shouldShow;
        stickyBar.classList.toggle('reeve-cart-sticky-bar--visible', shouldShow);
        stickyBar.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
      }
    }

    // Initial measure + scroll/resize listeners (throttled passive)
    measureTargets();
    updateVisibility();

    window.addEventListener('scroll', throttle(updateVisibility, 80), { passive: true });
    window.addEventListener('resize', throttle(function () {
      measureTargets();
      updateVisibility();
    }, 200));

    // Re-measure on cart updates (line remove changes layout geometry)
    window.addEventListener('reeve:cart:update', function (ev) {
      measureTargets();
      updateVisibility();
      if (ev.detail && ev.detail.cart && stickyTotal) {
        stickyTotal.textContent = window.REEVE.utils.formatCurrency(ev.detail.cart.total_price);
      }
    });

    // Click delegate: trigger click on main checkout CTA (shared logic — disabled toast + event)
    stickyCta.addEventListener('click', function (e) {
      e.preventDefault();
      var mainCta = document.querySelector('[data-reeve-checkout-cta]');
      if (!mainCta) return;
      mainCta.click();
    });
  }

  /**
   * Delivery timer — cutoff countdown + ETA chips + progress bar.
   * Refs: Design_Pages §4.7, Components §13 §14, Architecture §6.1.
   *
   * Generic mount: iterates document-wide over [data-reeve-delivery-timer-mount] wrappers
   * (PDP delivery section, cart delivery preview post-3.3.2). Each mount carries its own
   * config via data-* attrs (cutoffs, holidays, free threshold, delivery prices) and
   * contains children: [data-reeve-delivery-card="paczkomat|courier"] with ETA chip slots,
   * [data-reeve-delivery-timer] (primary copy), [data-reeve-delivery-holiday-warning].
   * Progress bar [data-reeve-progress] is optional — scoped to root, so cart mount (with
   * progress bar rendered outside the wrapper) skips the timer's progress logic and leaves
   * it to modules.cart.updateDeliveryCards (hotfix #3).
   *
   * Timer updates every 60 s (brand decision — no seconds, no FOMO).
   * Different delivery logic: InPost ships Saturdays, Courier weekdays only.
   * Polish grammar: "we wtorek" exception (accusative case).
   */
  function initDeliveryTimer() {
    var mounts = document.querySelectorAll('[data-reeve-delivery-timer-mount]');
    if (!mounts.length) return;
    mounts.forEach(initMount);

    function initMount(root) {
      // --- Config from data attributes (theme settings server-side) ---
      var config = parseDeliveryConfig(root);

      // --- DOM refs ---
      var inpostEta = root.querySelector('[data-reeve-delivery-card="paczkomat"] [data-reeve-delivery-eta]');
      var courierEta = root.querySelector('[data-reeve-delivery-card="courier"] [data-reeve-delivery-eta]');
      var countdownEl = root.querySelector('[data-reeve-delivery-timer]');

      // --- Polish day names (accusative case) — hardcoded, grammar can't be i18n'd simply ---
      // Components §13, Design_Pages §4.7
      var POLISH_DAYS = {
        0: 'w niedziel\u0119',
        1: 'w poniedzia\u0142ek',
        2: 'we wtorek',       // EXCEPTION — "we" not "w"
        3: 'w \u015brod\u0119',
        4: 'w czwartek',
        5: 'w pi\u0105tek',
        6: 'w sobot\u0119'
      };

      // --- Polish months genitive (declension) — hardcoded grammar, not i18n-able Faza 1.
      // Design_Pages §4.7 — primary copy format "21 kwietnia".
      var POLISH_MONTHS_GENITIVE = [
        'stycznia', 'lutego', 'marca', 'kwietnia', 'maja', 'czerwca',
        'lipca', 'sierpnia', 'wrze\u015bnia', 'pa\u017adziernika', 'listopada', 'grudnia'
      ];

      // Format date as "{D} {miesiąc_dopełniacz}" — no leading zero, Polish genitive.
      function formatPolishDate(date) {
        return date.getDate() + ' ' + POLISH_MONTHS_GENITIVE[date.getMonth()];
      }

      // getWarsawDate — force Europe/Warsaw wall clock regardless of browser TZ.
      // Shopify.shop.timezone is not exposed client-side; customers abroad still need PL warehouse hours.
      // Fallback: raw Date if Intl/timeZone support missing (very old browsers).
      function getWarsawDate(now) {
        try {
          var parts = new Intl.DateTimeFormat('en-CA', {
            timeZone: 'Europe/Warsaw',
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', second: '2-digit',
            hour12: false
          }).formatToParts(now);
          var map = {};
          for (var i = 0; i < parts.length; i++) {
            if (parts[i].type !== 'literal') map[parts[i].type] = parts[i].value;
          }
          return new Date(
            parseInt(map.year, 10),
            parseInt(map.month, 10) - 1,
            parseInt(map.day, 10),
            parseInt(map.hour, 10) % 24,
            parseInt(map.minute, 10),
            parseInt(map.second, 10)
          );
        } catch (e) {
          return now;
        }
      }

      // findClosestHolidayInWindow — earliest holiday within [today, today+daysAhead inclusive].
      // null if window clean. Evaluated once on mount for the 7-day holiday warning.
      function findClosestHolidayInWindow(now, daysAhead) {
        var today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        var horizon = new Date(today);
        horizon.setDate(horizon.getDate() + daysAhead);
        var closest = null;
        for (var i = 0; i < config.holidays.length; i++) {
          var parts = config.holidays[i].split('-');
          if (parts.length !== 3) continue;
          var h = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
          if (isNaN(h.getTime())) continue;
          if (h >= today && h <= horizon) {
            if (!closest || h < closest) closest = h;
          }
        }
        return closest;
      }

      function parseDeliveryConfig(el) {
        var holidaysRaw = el.dataset.reeveHolidays || '';
        return {
          cutoffMonThu: parseHour(el.dataset.reeveCutoffMonThu || '14:00'),
          cutoffFri: parseHour(el.dataset.reeveCutoffFri || '13:00'),
          cutoffSat: parseHour(el.dataset.reeveCutoffSat || '12:00'),
          holidays: holidaysRaw.split(/[\r\n]+/).map(function (s) { return s.trim(); }).filter(Boolean),
          freeThreshold: parseInt(el.dataset.reeveFreeThreshold) || 9999,
          paczkomatPrice: parseInt(el.dataset.reevePaczkomatPrice) || 1299,
          courierPrice: parseInt(el.dataset.reeveCourierPrice) || 999
        };
      }

      function parseHour(str) {
        var parts = str.split(':').map(Number);
        return { hour: parts[0] || 0, minute: parts[1] || 0 };
      }

      function isHoliday(date) {
        var ymd = date.getFullYear() + '-' +
          String(date.getMonth() + 1).padStart(2, '0') + '-' +
          String(date.getDate()).padStart(2, '0');
        return config.holidays.indexOf(ymd) !== -1;
      }

      /**
       * Hotfix #3 Change 4: weekend logic overhaul.
       * Get cutoff time dla danego dnia tygodnia. Niedziela = null (brak wysyłki).
       */
      function getCutoffForDow(dow) {
        if (dow >= 1 && dow <= 4) return config.cutoffMonThu; // pn-cz 14:00
        if (dow === 5) return config.cutoffFri;               // pt 13:00
        if (dow === 6) return config.cutoffSat;               // sb 12:00
        return null;                                          // nd — brak cutoff
      }

      /**
       * Znajdź najbliższy working day od podanej daty (inclusive lub exclusive z startNextDay).
       * Working day = nie niedziela, nie święto.
       */
      function findNextWorkingDay(fromDate, startNextDay) {
        var d = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate());
        if (startNextDay) d.setDate(d.getDate() + 1);
        while (d.getDay() === 0 || isHoliday(d)) {
          d.setDate(d.getDate() + 1);
        }
        return d;
      }

      /**
       * Delivery date = shipping + 1, skip sb + nd + holidays.
       * Nowa reguła (hotfix #3): InPost i Kurier tak samo — weekend = magazyn, delivery = pon.
       * pt shipping → delivery pon (sb skip, nd skip, pon OK).
       * sb shipping → delivery pon (nd skip, pon OK).
       * cz shipping → delivery pt (pt OK, nie weekend).
       */
      function getDeliveryDate(shippingDate) {
        var d = new Date(shippingDate.getFullYear(), shippingDate.getMonth(), shippingDate.getDate());
        d.setDate(d.getDate() + 1);
        while (d.getDay() === 0 || d.getDay() === 6 || isHoliday(d)) {
          d.setDate(d.getDate() + 1);
        }
        return d;
      }

      /**
       * Główna funkcja logiki hotfix #3: zwraca stan wysyłki + dostawy + timer info.
       * Returns: {
       *   timerVisible: bool (true jeśli today przed cutoff i nie święto/nd),
       *   hours, mins: countdown (tylko jeśli timerVisible),
       *   shippingDate: Date dzień wysyłki,
       *   deliveryDate: Date dzień dostawy,
       *   shippingIsToday: bool,
       *   shippingIsTomorrow: bool
       * }
       */
      function getShippingState(now) {
        var dow = now.getDay();
        var todayCutoffCfg = getCutoffForDow(dow);
        var todayIsHoliday = isHoliday(now);

        var shippingDate = null;
        var timerVisible = false;
        var hours = 0, mins = 0;

        if (todayCutoffCfg && !todayIsHoliday) {
          var cutoffDt = new Date(now.getFullYear(), now.getMonth(), now.getDate(),
                                  todayCutoffCfg.hour, todayCutoffCfg.minute);
          if (cutoffDt > now) {
            // Przed cutoff dziś — timer visible, wysyłka dziś
            shippingDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            timerVisible = true;
            var diffMs = cutoffDt - now;
            var totalMin = Math.floor(diffMs / 60000);
            hours = Math.floor(totalMin / 60);
            mins = totalMin % 60;
          }
        }

        if (!shippingDate) {
          // Po cutoff albo nd albo święto — wysyłka w najbliższy working day od jutra
          shippingDate = findNextWorkingDay(now, true);
        }

        var deliveryDate = getDeliveryDate(shippingDate);

        // shippingIsToday / isTomorrow
        function ymd(d) { return d.getFullYear() + '-' + d.getMonth() + '-' + d.getDate(); }
        var shipYmd = ymd(shippingDate);
        var shippingIsToday = ymd(now) === shipYmd;
        var tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
        var shippingIsTomorrow = ymd(tomorrow) === shipYmd;

        return {
          timerVisible: timerVisible,
          hours: hours,
          mins: mins,
          shippingDate: shippingDate,
          deliveryDate: deliveryDate,
          shippingIsToday: shippingIsToday,
          shippingIsTomorrow: shippingIsTomorrow
        };
      }

      /**
       * Render chip HTML — mirrors snippets/reeve-chip.liquid success variant.
       * Used for ETA chips (JS-rendered, dynamic).
       */
      function renderEtaChip(text) {
        return '<span class="reeve-chip reeve-chip--success">' +
          REEVE_ICONS.check +
          '<span>' + escapeHtml(text) + '</span>' +
        '</span>';
      }

      // --- Timer update (every 60 s) — hotfix #3 Change 4 overhaul ---
      // Primary copy (task 3.3): "{wysyłka}, dostawa {dzień} {DD} {miesiąc}" — Design_Pages §4.7.
      // ETA chip zostaje kompaktowy (bez daty) — mobile width constraint, Components §13.5.
      function updateTimer() {
        // Task 3.3: Warsaw-TZ wrap — customers abroad still see PL warehouse clock.
        var now = getWarsawDate(new Date());
        var state = getShippingState(now);

        var deliveryDay = POLISH_DAYS[state.deliveryDate.getDay()];
        var deliveryDate = formatPolishDate(state.deliveryDate);

        // --- Primary line ---
        var primaryText = '';
        if (state.timerVisible) {
          if (state.hours === 0 && state.mins === 0) {
            // Exact-cutoff edge (≤1 min window). Keep dateless — next tick flips to *_with_date.
            primaryText = t('reeve.shipping.order_now');
          } else if (state.hours === 0) {
            primaryText = t('reeve.shipping.order_within_minutes_with_date', {
              minutes: state.mins,
              delivery_day: deliveryDay,
              delivery_date: deliveryDate
            });
          } else {
            primaryText = t('reeve.shipping.order_within_hours_with_date', {
              hours: state.hours,
              minutes: state.mins,
              delivery_day: deliveryDay,
              delivery_date: deliveryDate
            });
          }
        } else if (state.shippingIsTomorrow && now.getDay() >= 1 && now.getDay() <= 5) {
          // "Wysyłka jutro" gdy today jest pn-pt — jutro jest dniem wysyłki (pn-cz→cz-pt, pt→sb).
          // BuildPlan §3.3 test 3 — pt post-cutoff → "jutro" (sb też ships). Sb post-cutoff i nd
          // spadają do shipping_on_day (sb→pon = +2 dni; nd→pon też pokazujemy "w poniedziałek").
          primaryText = t('reeve.shipping.shipping_tomorrow_with_date', {
            delivery_day: deliveryDay,
            delivery_date: deliveryDate
          });
        } else {
          primaryText = t('reeve.shipping.shipping_on_day_with_date', {
            shipping_day: POLISH_DAYS[state.shippingDate.getDay()],
            delivery_day: deliveryDay,
            delivery_date: deliveryDate
          });
        }

        if (countdownEl) {
          countdownEl.innerHTML = REEVE_ICONS.clock + ' ' + escapeHtml(primaryText);
        }

        // --- ETA chips on cards (both InPost and Courier — same logic post hotfix #3).
        // Task 3.3: dateless on purpose — chip width constrained on mobile (Components §13.5). ---
        var etaChipText = t('reeve.shipping.delivery_on_day', { day: deliveryDay });
        if (inpostEta) inpostEta.innerHTML = renderEtaChip(etaChipText);
        if (courierEta) courierEta.innerHTML = renderEtaChip(etaChipText);
      }

      // Holiday warning (task 3.3) — evaluate once on mount, show if a holiday falls within +7 days.
      // Date doesn't change mid-session; no re-eval in interval.
      function evaluateHolidayWarning() {
        var warningEl = root.querySelector('[data-reeve-delivery-holiday-warning]');
        if (!warningEl) return;
        var now = getWarsawDate(new Date());
        var holiday = findClosestHolidayInWindow(now, 7);
        if (!holiday) {
          warningEl.hidden = true;
          return;
        }
        var text = t('reeve.shipping.holiday_warning', { date: formatPolishDate(holiday) });
        warningEl.innerHTML = REEVE_ICONS.info + '<span>' + escapeHtml(text) + '</span>';
        warningEl.hidden = false;
      }

      // --- Progress bar update (on cart events) ---
      // Scoped to root — cart mount has progress bar OUTSIDE the mount wrapper, so
      // root.querySelector returns null there and cart skips this path (modules.cart
      // handles cart progress bar + delivery card GRATIS sync via hotfix #3).
      function updateProgressBar() {
        var bar = root.querySelector('[data-reeve-progress]');
        if (!bar) return;

        fetch('/cart.js')
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            var threshold = config.freeThreshold;
            var total = cart.total_price;
            var pct = Math.min(100, Math.round((total / threshold) * 100));
            var remaining = Math.max(0, threshold - total);
            var reached = total >= threshold;

            var fill = bar.querySelector('.reeve-progress-bar__fill');
            var textEl = bar.querySelector('[data-reeve-progress-text]');

            if (fill) {
              fill.style.width = pct + '%';
              fill.setAttribute('aria-valuenow', String(pct));
            }

            if (textEl) {
              if (reached) {
                textEl.innerHTML = REEVE_ICONS.check + ' ' +
                  escapeHtml(t('reeve.shipping.free_threshold_reached'));
              } else {
                var amountFormatted = formatCurrency(remaining);
                textEl.innerHTML = t('reeve.shipping.free_threshold_below_html', {
                  amount: '<strong>' + escapeHtml(amountFormatted) + '</strong>'
                });
              }
            }

            // Toggle GRATIS display on delivery cards
            var cards = root.querySelectorAll('[data-reeve-delivery-card]');
            cards.forEach(function (card) {
              var method = card.dataset.reeveDeliveryCard;
              var priceEl = card.querySelector('[data-reeve-delivery-price]');
              if (!priceEl) return;

              var wasFree = card.classList.contains('reeve-delivery-card--free-shipping');
              if (reached && !wasFree) {
                card.classList.add('reeve-delivery-card--free-shipping');
                priceEl.innerHTML = '<span class="reeve-chip reeve-chip--success">' +
                  REEVE_ICONS.check +
                  '<span>' + escapeHtml(t('reeve.shipping.free_chip')) + '</span></span>';
              } else if (!reached && wasFree) {
                card.classList.remove('reeve-delivery-card--free-shipping');
                var price = method === 'paczkomat' ? config.paczkomatPrice : config.courierPrice;
                priceEl.innerHTML = '<span class="reeve-delivery-card__price-amount">' +
                  escapeHtml(formatCurrency(price)) + '</span>';
              }
            });
          })
          .catch(function (err) {
            console.warn('[REEVE.deliveryTimer] progress bar update failed', err);
          });
      }

      // --- Init ---
      updateTimer();
      updateProgressBar();
      evaluateHolidayWarning(); // Task 3.3: once on mount; date doesn't change mid-session.
      setInterval(updateTimer, 60000); // 60 s — brand decision, no seconds. Design_Pages §4.7.

      // Cart event listeners — Horizon-compatible (document-scope) + REEVE Customer Event (window-scope)
      // Hotfix cart polish Bug 3: naming z 'reeve:cart-update' (dash, legacy typo) na
      // 'reeve:cart:update' (colon) — aktualny naming per CLAUDE.md §11.5 lekcja 22
      // (window.REEVE namespace lowercase dotted, NIE SHOUTING_CASE). Buybox L742 + modules.cart
      // L1006 dispatch z colon. Dash byl misnamed i event nie byl lapany → delivery cards nie sync.
      document.addEventListener('cart:update', function () { updateProgressBar(); });
      window.addEventListener('reeve:cart:update', function () { updateProgressBar(); });
    }
  }

  /**
   * Toast notification controller — single-slot, auto-close, reduced-motion aware.
   * Refs: Architecture §4, Design_Pages §4.11.1 (sticky CTA post-add feedback).
   *
   * Public API exported via window.REEVE.utils.showToast(message, type, duration, options):
   *   type:     'success' | 'error' | 'info' (default 'info')
   *   duration: ms (default 3000, 0 = manual dismiss)
   *   options:  { action?: { label: string, callback: () => void } }
   *             — gdy action podany, toast ma button "label"; click → callback() + dismiss.
   *             Uzywane przez modules.cart removeItem (task 2.20 — "Cofnij" undo flow).
   *
   * Reused by: modules.buybox (add-to-cart feedback), modules.pdpStickyCta,
   * modules.cart (task 2.20), variant picker unavailable warnings.
   * Container is lazy-created on first call — section does not need to render it.
   */
  function initToast() {
    var container = null;
    var currentToast = null;
    var currentTimeout = null;
    var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function ensureContainer() {
      if (container) return container;
      container = document.querySelector('[data-reeve-toast-container]');
      if (container) return container;
      container = document.createElement('div');
      container.className = 'reeve-toast-container';
      container.setAttribute('data-reeve-toast-container', '');
      container.setAttribute('aria-live', 'polite');
      container.setAttribute('aria-atomic', 'true');
      document.body.appendChild(container);
      return container;
    }

    // Hotfix ATC feedback (2026-04-22): mierzymy viewport-relative dol announcement+header
    // przy kazdym showToast() i ustawiamy --reeve-toast-top inline, zeby toast
    // zawsze ladowal ponizej sticky headera (bug: static top:16px renderowal toast
    // pod barem announcement+header). Measure-once per show (bez scroll listenera).
    function updateTopOffset(root) {
      var announcement = document.querySelector('[data-reeve-announcement-bar]');
      var header = document.querySelector('[data-reeve-header]');
      var announcementBottom = announcement ? announcement.getBoundingClientRect().bottom : 0;
      var headerBottom = header ? header.getBoundingClientRect().bottom : 0;
      var topPx = Math.max(announcementBottom, headerBottom, 0) + 16;
      root.style.setProperty('--reeve-toast-top', topPx + 'px');
    }

    function showToast(message, type, duration, options) {
      type = type || 'info';
      if (typeof duration !== 'number') duration = 3000;

      var root = ensureContainer();
      updateTopOffset(root);

      // Replace current toast (max 1 on screen at a time)
      if (currentToast) {
        currentToast.remove();
        if (currentTimeout) {
          clearTimeout(currentTimeout);
          currentTimeout = null;
        }
      }

      var toast = document.createElement('div');
      toast.className = 'reeve-toast reeve-toast--' + type;
      toast.setAttribute('role', type === 'error' ? 'alert' : 'status');

      var hasAction = options && options.action && typeof options.action.label === 'string' && typeof options.action.callback === 'function';
      if (hasAction) {
        toast.classList.add('reeve-toast--has-action');
        var msgSpan = document.createElement('span');
        msgSpan.className = 'reeve-toast__message';
        msgSpan.textContent = message;
        toast.appendChild(msgSpan);

        var actionBtn = document.createElement('button');
        actionBtn.type = 'button';
        actionBtn.className = 'reeve-toast__action';
        actionBtn.textContent = options.action.label;
        actionBtn.addEventListener('click', function () {
          try {
            options.action.callback();
          } catch (err) {
            console.error('[reeve] toast action callback threw:', err);
          }
          dismiss(toast);
        });
        toast.appendChild(actionBtn);
      } else {
        toast.textContent = message;
      }

      root.appendChild(toast);
      currentToast = toast;

      // Trigger slide-in (skip animation under reduced-motion)
      if (prefersReducedMotion) {
        toast.classList.add('reeve-toast--visible');
      } else {
        requestAnimationFrame(function () {
          toast.classList.add('reeve-toast--visible');
        });
      }

      if (duration > 0) {
        currentTimeout = setTimeout(function () {
          dismiss(toast);
        }, duration);
      }

      return toast;
    }

    function dismiss(toast) {
      if (!toast) return;
      toast.classList.remove('reeve-toast--visible');
      var cleanup = function () {
        toast.remove();
        if (currentToast === toast) currentToast = null;
      };
      if (prefersReducedMotion) {
        cleanup();
      } else {
        setTimeout(cleanup, 260); // match CSS transition 250ms + 10ms slack
      }
    }

    // Export via Object.assign (CLAUDE.md §11.5 rule #10 — never overwrite)
    window.REEVE = window.REEVE || {};
    window.REEVE.utils = Object.assign(window.REEVE.utils || {}, {
      showToast: showToast
    });
  }

  /**
   * General-purpose modal controller — open/close/focus-trap/ESC/backdrop-click/body-scroll-lock.
   * Reused by productGallery lightbox, future confirm dialogs, size guide (2.13+).
   * Pattern reuse from initCollectionFilters drawer (lines 835-876).
   * Refs: Components §16, Architecture §4.
   */
  function initModal() {
    var modals = document.querySelectorAll('[data-reeve-modal]');
    if (!modals.length) return;

    var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
    var activeModal = null;
    var lastFocus = null;

    function getFocusables(modal) {
      return Array.from(modal.querySelectorAll(FOCUSABLE)).filter(function (el) {
        return el.offsetParent !== null;
      });
    }

    function onModalKey(e) {
      if (!activeModal) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        closeModal();
        return;
      }
      if (e.key !== 'Tab') return;
      var focusables = getFocusables(activeModal);
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        last.focus();
        e.preventDefault();
      } else if (!e.shiftKey && document.activeElement === last) {
        first.focus();
        e.preventDefault();
      }
    }

    function openModal(idOrEl) {
      var modal = typeof idOrEl === 'string' ? document.getElementById(idOrEl) : idOrEl;
      if (!modal) return;
      lastFocus = document.activeElement;
      modal.removeAttribute('hidden');
      document.body.classList.add('reeve-scroll-lock');
      activeModal = modal;
      document.addEventListener('keydown', onModalKey);
      // Focus first focusable (close button typically)
      var closeBtn = modal.querySelector('[data-reeve-modal-close]');
      var firstFocusable = closeBtn || modal.querySelector(FOCUSABLE);
      if (firstFocusable) firstFocusable.focus();
    }

    function closeModal() {
      if (!activeModal) return;
      activeModal.setAttribute('hidden', '');
      document.body.classList.remove('reeve-scroll-lock');
      document.removeEventListener('keydown', onModalKey);
      activeModal = null;
      if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
      lastFocus = null;
    }

    // Wire close triggers (backdrop + close buttons)
    modals.forEach(function (modal) {
      modal.querySelectorAll('[data-reeve-modal-close]').forEach(function (trigger) {
        trigger.addEventListener('click', function (e) {
          // Only close if clicking the backdrop itself or the close button (not children of body)
          if (trigger.classList.contains('reeve-modal__backdrop') || trigger.classList.contains('reeve-modal__close')) {
            e.preventDefault();
            if (activeModal === modal) closeModal();
          }
        });
      });
    });

    // Expose open/close API for other modules (productGallery lightbox)
    window.REEVE._modalOpen = openModal;
    window.REEVE._modalClose = closeModal;
  }

  /* Architecture §4 — smooth height animation enhancement for native <details>.
     Baseline: <details>/<summary> works without JS (keyboard, a11y, toggle).
     Enhancement: smooth expand/collapse animation (180ms base duration).
     prefers-reduced-motion: reduce — skip enhancement, native abrupt toggle. */
  function initAccordion() {
    var accordions = document.querySelectorAll('[data-reeve-accordion]');
    if (!accordions.length) return;

    var motionOk = window.matchMedia('(prefers-reduced-motion: no-preference)').matches;
    if (!motionOk) return;

    accordions.forEach(function(details) {
      var summary = details.querySelector('summary');
      var answer = details.querySelector('.reeve-accordion__answer');
      if (!summary || !answer) return;

      var animating = false;

      summary.addEventListener('click', function(e) {
        e.preventDefault();
        if (animating) return;
        animating = true;

        if (details.open) {
          // Closing: animate height to 0, then remove open
          var startHeight = answer.scrollHeight;
          answer.style.overflow = 'hidden';
          answer.style.height = startHeight + 'px';
          answer.offsetHeight; // force reflow
          answer.style.transition = 'height var(--reeve-duration-base) var(--reeve-ease-out)';
          answer.style.height = '0';

          answer.addEventListener('transitionend', function handler() {
            answer.removeEventListener('transitionend', handler);
            details.open = false;
            answer.style.height = '';
            answer.style.overflow = '';
            answer.style.transition = '';
            animating = false;
          });
          setTimeout(function() { animating = false; }, 250);
        } else {
          // Opening: set open to measure, then animate from 0 to target
          details.open = true;
          var targetHeight = answer.scrollHeight;
          answer.style.overflow = 'hidden';
          answer.style.height = '0';
          answer.offsetHeight; // force reflow
          answer.style.transition = 'height var(--reeve-duration-base) var(--reeve-ease-out)';
          answer.style.height = targetHeight + 'px';

          answer.addEventListener('transitionend', function handler() {
            answer.removeEventListener('transitionend', handler);
            answer.style.height = '';
            answer.style.overflow = '';
            answer.style.transition = '';
            animating = false;
          });
          setTimeout(function() { animating = false; }, 250);
        }
      });
    });
  }

  // Task 4.8 — Google Merchant Center widget iframe (#merchantwidgetiframe) wstawiany
  // w runtime przez Shopify "Google & YouTube" app, poza kodem repo. Missing `title`
  // attribute → axe frame-title fail na wszystkich 5 stronach z audytu. Patch ustawia
  // title po załadowaniu iframe w DOM (guard: nie nadpisuje jeśli app kiedyś doda własny).
  // Key z window.REEVE.translations (reeve-translations.liquid) + polski fallback dla stron
  // które nie renderują translations snippet (home, collection, kontakt).
  // Architecture §4 — registered as merchantWidgetA11y w REEVE.modules.
  function initMerchantWidgetA11y() {
    var IFRAME_ID = 'merchantwidgetiframe';
    var KEY = 'reeve.a11y.google_merchant_widget_title';
    // PL fallback — sklep reeve.pl jest polish-only (REEVE_i18n_Markets.md). Przy ekspansji
    // na inne rynki: snippet reeve-translations musi być renderowany globally.
    var FALLBACK = 'Opinie Google Merchant Center';
    var store = (window.REEVE && window.REEVE.translations) || {};
    var title = store[KEY] || FALLBACK;

    function apply(iframe) {
      if (iframe && !iframe.getAttribute('title')) {
        iframe.setAttribute('title', title);
      }
    }

    apply(document.getElementById(IFRAME_ID));

    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.id === IFRAME_ID) {
            apply(node);
            return;
          }
          if (typeof node.querySelector === 'function') {
            var nested = node.querySelector('#' + IFRAME_ID);
            if (nested) {
              apply(nested);
              return;
            }
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  // Design_Pages §3.6 (task 2.10) — collection load-more pagination.
  // AJAX fetch paginate.next.url przez Section Rendering API, append cards do grid.
  // Re-bind po filter update z modules.collectionFilters (event reeve:collection-updated).
  // prefers-reduced-motion aware (skeleton bez pulse, append bez transitions — CSS).
  function initPagination() {
    const SECTION_ID = 'reeve-collection-filters';
    const GRID_ITEMS_SELECTOR = '[data-reeve-grid] .reeve-collection-grid__items';
    const SKELETON_TEMPLATE_SELECTOR = '[data-reeve-grid-skeleton] > *';

    let isLoading = false;

    function bind(paginationRoot) {
      if (!paginationRoot) return;
      const btn = paginationRoot.querySelector('[data-reeve-load-more]');
      if (!btn || btn.dataset.reeveBound === 'true') return;
      btn.dataset.reeveBound = 'true';
      btn.addEventListener('click', function () {
        loadMore(paginationRoot, btn);
      });
    }

    async function loadMore(paginationRoot, btn) {
      if (isLoading) return;
      const nextUrl = btn.getAttribute('data-next-url');
      if (!nextUrl) return;

      const grid = document.querySelector(GRID_ITEMS_SELECTOR);
      if (!grid) return;

      isLoading = true;
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');

      const skeletons = appendSkeletons(grid, 8);

      try {
        const fetchUrl = new URL(nextUrl, window.location.origin);
        fetchUrl.searchParams.set('section_id', SECTION_ID);
        const response = await fetch(fetchUrl.toString(), { headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('[REEVE.pagination] ' + response.status);
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');

        const newItems = doc.querySelectorAll('[data-reeve-grid] .reeve-collection-grid__items > *');
        skeletons.forEach(function (s) { s.remove(); });
        newItems.forEach(function (item) {
          grid.appendChild(item.cloneNode(true));
        });

        const newPagination = doc.querySelector('[data-reeve-pagination]');
        if (newPagination) {
          paginationRoot.replaceWith(newPagination.cloneNode(true));
          bind(document.querySelector('[data-reeve-pagination]'));
        } else {
          paginationRoot.remove();
        }
      } catch (e) {
        console.warn('[REEVE.pagination]', e);
        skeletons.forEach(function (s) { s.remove(); });
        btn.disabled = false;
        btn.removeAttribute('aria-busy');
      } finally {
        isLoading = false;
      }
    }

    function appendSkeletons(grid, count) {
      const tpl = document.querySelector(SKELETON_TEMPLATE_SELECTOR);
      if (!tpl) return [];
      const nodes = [];
      for (let i = 0; i < count; i++) {
        const node = tpl.cloneNode(true);
        node.setAttribute('data-reeve-load-more-skeleton', '');
        grid.appendChild(node);
        nodes.push(node);
      }
      return nodes;
    }

    bind(document.querySelector('[data-reeve-pagination]'));

    // Re-bind po filter update z modules.collectionFilters — nowy button może mieć nowy next_url.
    document.addEventListener('reeve:collection-updated', function () {
      bind(document.querySelector('[data-reeve-pagination]'));
    });
  }

  function initAnalytics() {
    const root = document.querySelector('[data-reeve-analytics]');
    if (!root) return;
    /* TBD: customEvents dispatch dla GA4/Pixel */
  }

  // Design_Pages §1.2 — announcement bar: 2 messages rotating 6s with crossfade.
  // prefers-reduced-motion: reduce → no rotation (message 0 stays visible).
  function initAnnouncementBar() {
    const root = document.querySelector('[data-reeve-announcement-bar]');
    if (!root) return;
    const messages = root.querySelectorAll('[data-reeve-announcement-message]');
    if (messages.length < 2) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let active = 0;
    setInterval(function () {
      messages[active].classList.remove('is-active');
      active = (active + 1) % messages.length;
      messages[active].classList.add('is-active');
    }, 6000);
  }

  // Design_Pages §1.6 — carousel for "Promocje" rail. Native scroll API + scroll-snap, zero deps.
  // Mobile używa natywnego touch swipe (scroll-snap-type: x mandatory) — JS tylko dla strzałek desktop.
  // Task 2.18 (D1-B): selektor rozszerzony o [data-reeve-product-rail-upsell] — reuse dla reeve-pdp-upsell
  // bez wydzielania osobnej funkcji. Idempotency guard pozwala bezpiecznie re-init po fetch injection.
  function initProductRail() {
    const rails = document.querySelectorAll(
      '[data-reeve-home-product-rail][data-layout="carousel"], [data-reeve-product-rail-upsell][data-layout="carousel"]'
    );
    if (!rails.length) return;
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    rails.forEach((rail) => {
      // Idempotency — chronimy przed duplikacją listenerów po re-init z pdpUpsell inject
      if (rail.dataset.reeveRailInitialized === 'true') return;

      const cards = rail.querySelector('[data-reeve-rail-cards]');
      const prev = rail.querySelector('[data-reeve-rail-arrow="prev"]');
      const next = rail.querySelector('[data-reeve-rail-arrow="next"]');
      if (!cards || !prev || !next) return;

      // Skip gdy brak realnych kart (pdp-upsell w trybie skeleton przed fetch replace) — re-init po inject przejdzie
      if (!cards.querySelector('.reeve-product-card')) return;

      rail.dataset.reeveRailInitialized = 'true';

      const getStep = () => {
        const first = cards.querySelector('.reeve-product-card');
        if (!first) return cards.clientWidth;
        const gap = parseFloat(getComputedStyle(cards).gap) || 0;
        return first.getBoundingClientRect().width + gap;
      };

      const updateDisabled = () => {
        // Hide arrows entirely when all cards fit without scrolling.
        // Hotfix #2 (PDP 2.18): disabled-only was creating visual ghost; hidden = no ghost.
        // Hotfix cart polish (Bug 5): tolerance podniesiona z 1 do 4px — flex calc
        // z kilkoma kartami moze dawac sub-pixel rounding scrollWidth > clientWidth o 1-2px,
        // ktore oszukiwalo 'noScroll' check i pokazywalo ghost arrows przy 4 kartach @1440px.
        const noScroll = cards.scrollWidth <= cards.clientWidth + 4;
        if (noScroll) {
          prev.hidden = true;
          next.hidden = true;
          return;
        }
        prev.hidden = false;
        next.hidden = false;
        const atStart = cards.scrollLeft <= 1;
        const atEnd = cards.scrollLeft + cards.clientWidth >= cards.scrollWidth - 1;
        prev.toggleAttribute('disabled', atStart);
        next.toggleAttribute('disabled', atEnd);
      };

      const scrollByDir = (dir) => {
        cards.scrollBy({
          left: dir * getStep(),
          behavior: prefersReduced ? 'auto' : 'smooth',
        });
      };

      prev.addEventListener('click', () => scrollByDir(-1));
      next.addEventListener('click', () => scrollByDir(1));
      cards.addEventListener('scroll', debounce(updateDisabled, 100));
      window.addEventListener('resize', debounce(updateDisabled, 200));
      updateDisabled();
    });
  }

  // Design_Pages §4.10.1 (task 2.18) — PDP cross-sell "Najczesciej razem z tym produktem".
  // Section Rendering API: fetch /recommendations/products?section_id=<id>&product_id=<pid>&intent=related&limit=N.
  // Shopify renderuje sekcje z recommendations.performed? = true; Liquid dokłada L2/L3 fallback.
  // Po replace container HTML wołamy initProductRail() — idempotency guard chroni istniejące home-rails.
  // Sekcja hidden (root.remove) gdy: fetch error, brak container w response, <2 kart.
  function initPdpUpsell() {
    const root = document.querySelector('[data-reeve-pdp-upsell]');
    if (!root) return;

    const productId = root.dataset.productId;
    const sectionId = root.dataset.sectionId;
    const limit = parseInt(root.dataset.limit, 10) || 4;
    const layout = root.dataset.layout || 'carousel';
    const container = root.querySelector('[data-reeve-upsell-container]');
    if (!container || !productId || !sectionId) return;

    const url = `/recommendations/products?section_id=${encodeURIComponent(sectionId)}&product_id=${encodeURIComponent(productId)}&limit=${limit}&intent=related`;

    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error('[REEVE.pdpUpsell] HTTP ' + response.status);
        return response.text();
      })
      .then((html) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newContainer = doc.querySelector('[data-reeve-upsell-container]');
        if (!newContainer) {
          root.remove();
          return;
        }
        const cards = newContainer.querySelectorAll('.reeve-product-card');
        if (cards.length < 2) {
          root.remove();
          return;
        }
        container.innerHTML = newContainer.innerHTML;

        // Re-init carousel arrows dla nowo wstrzyknietych kart (D1-B reuse).
        // Idempotency guard w initProductRail chroni home-rails przed duplikacja listenerow.
        if (layout === 'carousel') {
          initProductRail();
        }
      })
      .catch((err) => {
        console.warn('[REEVE.pdpUpsell] fetch failed, hiding section', err);
        root.remove();
      });
  }

  // Punkt w3.A (ratunek reeve.pl) — PDP cross-sell "Dobierz do kompletu".
  // Wzorzec kanoniczny Shopify: routes.product_recommendations_url (renderowany server-side
  // w data-reeve-cross-sell-url) + IntersectionObserver lazy fetch (rootMargin 200px, fetch
  // dopiero gdy sekcja wjezdza w viewport — inny mechanizm niz immediate-fetch initPdpUpsell).
  // Intent 'complementary' wymaga manualnej konfiguracji w Search & Discovery — gdy odpowiedz
  // nie zawiera zadnej karty, usuwamy root bez ponawiania (zero pustych naglowkow, zero
  // podmiany intencji marketingowej). Root usuwany tez przy HTTP/parse error.
  function initPdpCrossSell() {
    const root = document.querySelector('[data-reeve-pdp-cross-sell]');
    if (!root) return;

    const url = root.dataset.reeveCrossSellUrl;
    if (!url) return;

    const applyResponse = (html) => {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newRoot = doc.querySelector('[data-reeve-pdp-cross-sell]');
      const cards = newRoot ? newRoot.querySelectorAll('.reeve-product-card') : [];

      if (cards.length > 0) {
        root.innerHTML = newRoot.innerHTML;
        return;
      }

      root.remove();
    };

    const fetchAndApply = (fetchUrl) => {
      fetch(fetchUrl)
        .then((response) => {
          if (!response.ok) throw new Error('[REEVE.pdpCrossSell] HTTP ' + response.status);
          return response.text();
        })
        .then((html) => applyResponse(html))
        .catch((err) => {
          console.warn('[REEVE.pdpCrossSell] fetch failed, hiding section', err);
          root.remove();
        });
    };

    if (!('IntersectionObserver' in window)) {
      fetchAndApply(url);
      return;
    }

    const observer = new IntersectionObserver((entries, obs) => {
      if (!entries[0].isIntersecting) return;
      obs.unobserve(root);
      fetchAndApply(url);
    }, { rootMargin: '0px 0px 200px 0px' });
    observer.observe(root);
  }

  // Design_Pages §3.3 (task 2.9) — collection filters: sidebar desktop + mobile drawer
  // AJAX update przez Shopify Section Rendering API (?section_id=...), history.pushState,
  // debounced inputs, focus trap w drawerze, prefers-reduced-motion aware.
  function initCollectionFilters() {
    const root = document.querySelector('[data-reeve-collection-filters]');
    if (!root) return;

    const form = root.querySelector('[data-reeve-filters-form]');
    const sortForm = root.querySelector('[data-reeve-sort-form]');
    const sortSelect = root.querySelector('[data-reeve-sort-select]');
    const drawer = root.querySelector('[data-reeve-filters-drawer]');
    const drawerOverlay = root.querySelector('[data-reeve-drawer-overlay]');
    const openBtn = root.querySelector('[data-reeve-filters-open]');
    const closeBtn = root.querySelector('[data-reeve-filters-close]');
    const applyBtn = root.querySelector('[data-reeve-filters-apply]');
    const clearBtn = root.querySelector('[data-reeve-filters-clear]');
    const grid = root.querySelector('[data-reeve-grid]');
    const skeleton = root.querySelector('[data-reeve-grid-skeleton]');
    const countEl = root.querySelector('[data-reeve-products-count]');
    const mobileCountEl = root.querySelector('[data-reeve-filters-count]');
    const sectionId = 'reeve-collection-filters';

    if (!form || !grid) return;

    const mq = window.matchMedia('(min-width: 1024px)');
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

    let currentFetch = null;
    let drawerLastFocus = null;

    // ---- AJAX update via Section Rendering API ----
    async function updateFromUrl(url, pushState) {
      if (currentFetch) currentFetch.abort();
      const controller = new AbortController();
      currentFetch = controller;

      grid.setAttribute('data-reeve-grid-loading', 'true');

      try {
        const fetchUrl = new URL(url, window.location.origin);
        fetchUrl.searchParams.set('section_id', sectionId);
        const response = await fetch(fetchUrl.toString(), { signal: controller.signal });
        if (!response.ok) throw new Error('[REEVE.collectionFilters] ' + response.status);
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // Swap: filters form (counts, disabled states), active filters, grid, toolbar count, promoted
        swapRegion(doc, '[data-reeve-filters-form] .reeve-collection-filters__groups', '.reeve-collection-filters__groups');
        initPriceSliders();
        swapRegion(doc, '[data-reeve-active-filters]', '[data-reeve-active-filters]', true);
        swapRegion(doc, '[data-reeve-grid] .reeve-collection-grid__items', '.reeve-collection-grid__items', true);
        swapRegion(doc, '[data-reeve-grid] .reeve-collection-grid__empty', '.reeve-collection-grid__empty', true);
        swapRegion(doc, '[data-reeve-pagination]', '[data-reeve-pagination]', true);
        swapRegion(doc, '[data-reeve-products-count]', '[data-reeve-products-count]');

        // Update root active-count attribute from response section
        const newRoot = doc.querySelector('[data-reeve-collection-filters]');
        if (newRoot) {
          const newCount = newRoot.getAttribute('data-reeve-active-count') || '0';
          root.setAttribute('data-reeve-active-count', newCount);
          updateMobileCount(parseInt(newCount, 10));
        }

        // Sync sort form hidden inputs (active filters changed → hidden inputs need refresh)
        const newSortForm = doc.querySelector('[data-reeve-sort-form]');
        if (newSortForm && sortForm) {
          const oldHidden = sortForm.querySelectorAll('input[type="hidden"]');
          oldHidden.forEach((el) => el.remove());
          newSortForm.querySelectorAll('input[type="hidden"]').forEach((el) => {
            sortForm.insertBefore(el.cloneNode(true), sortForm.firstChild);
          });
        }

        if (pushState) {
          const displayUrl = new URL(url, window.location.origin);
          displayUrl.searchParams.delete('section_id');
          window.history.pushState({ reeveFilters: true }, '', displayUrl.toString());
        }

        // Task 2.10 — notify modules.pagination że grid został zresetowany.
        // Pagination re-bind na nowy button (lub usunięcie jeśli <24 wyników po filtrze).
        document.dispatchEvent(new CustomEvent('reeve:collection-updated'));
      } catch (e) {
        if (e.name !== 'AbortError') console.warn('[REEVE.collectionFilters]', e);
      } finally {
        grid.removeAttribute('data-reeve-grid-loading');
        currentFetch = null;
      }
    }

    function swapRegion(doc, srcSelector, dstSelector, optional) {
      const src = doc.querySelector(srcSelector);
      const dst = root.querySelector(dstSelector);
      if (src && dst) {
        dst.replaceWith(src);
      } else if (src && !dst) {
        // dst was conditionally absent (e.g. active-filters hidden) → insert in anchor
        if (dstSelector.includes('active-filters')) {
          const anchor = root.querySelector('.reeve-collection-filters__inner');
          if (anchor) anchor.insertBefore(src, anchor.querySelector('.reeve-collection-filters__layout'));
        } else if (dstSelector.includes('reeve-pagination')) {
          // Task 2.10 — pagination pojawia się po reset filtra (np. z <24 do >24 items).
          // Insert za grid__items wewnątrz [data-reeve-grid].
          const gridAnchor = root.querySelector('[data-reeve-grid] .reeve-collection-grid__items');
          if (gridAnchor && gridAnchor.parentNode) {
            gridAnchor.parentNode.insertBefore(src, gridAnchor.nextSibling);
          }
        }
      } else if (!src && dst && optional) {
        dst.remove();
      }
    }

    function updateMobileCount(count) {
      if (!mobileCountEl) return;
      mobileCountEl.textContent = '(' + count + ')';
      if (count > 0) {
        mobileCountEl.removeAttribute('hidden');
      } else {
        mobileCountEl.setAttribute('hidden', '');
      }
    }

    function buildUrlFromForm(formEl) {
      const fd = new FormData(formEl);
      const params = new URLSearchParams();
      fd.forEach((value, key) => {
        if (value !== '' && value != null) params.append(key, value);
      });
      // Preserve sort_by from current URL if not in form
      if (!params.has('sort_by')) {
        const currentSort = new URL(window.location.href).searchParams.get('sort_by');
        if (currentSort) params.set('sort_by', currentSort);
      }
      const collectionUrl = formEl.getAttribute('action') || window.location.pathname;
      const qs = params.toString();
      return qs ? collectionUrl + '?' + qs : collectionUrl;
    }

    // ---- Price range dual-slider sync (fix/2.9-polish) ----
    function initPriceSliders() {
      const containers = form.querySelectorAll('[data-reeve-price-range]');
      containers.forEach((container) => {
        if (container.dataset.reevePriceSliderInit === '1') return;
        container.dataset.reevePriceSliderInit = '1';

        const rangeMin = container.querySelector('[data-reeve-price-range-min]');
        const rangeMax = container.querySelector('[data-reeve-price-range-max]');
        const numMin = container.querySelector('[data-reeve-price-number-min]');
        const numMax = container.querySelector('[data-reeve-price-number-max]');
        const trackActive = container.querySelector('[data-reeve-price-track-active]');
        if (!rangeMin || !rangeMax || !numMin || !numMax || !trackActive) return;

        const MIN = parseInt(rangeMin.min, 10) || 0;
        const MAX = parseInt(rangeMin.max, 10) || 100;
        const range = MAX - MIN || 1;
        const MIN_GAP = 1;

        function paint() {
          const lo = parseInt(rangeMin.value, 10);
          const hi = parseInt(rangeMax.value, 10);
          const loPct = ((lo - MIN) / range) * 100;
          const hiPct = ((hi - MIN) / range) * 100;
          trackActive.style.left = loPct + '%';
          trackActive.style.right = (100 - hiPct) + '%';
        }

        function syncNumbersFromRange() {
          const lo = parseInt(rangeMin.value, 10);
          const hi = parseInt(rangeMax.value, 10);
          numMin.value = lo > MIN ? lo : '';
          numMax.value = hi < MAX ? hi : '';
        }

        function syncRangeFromNumbers() {
          const lo = numMin.value === '' ? MIN : Math.max(MIN, Math.min(MAX, parseInt(numMin.value, 10)));
          const hi = numMax.value === '' ? MAX : Math.max(MIN, Math.min(MAX, parseInt(numMax.value, 10)));
          rangeMin.value = Math.min(lo, hi - MIN_GAP);
          rangeMax.value = Math.max(hi, lo + MIN_GAP);
          paint();
        }

        rangeMin.addEventListener('input', () => {
          if (parseInt(rangeMin.value, 10) > parseInt(rangeMax.value, 10) - MIN_GAP) {
            rangeMin.value = parseInt(rangeMax.value, 10) - MIN_GAP;
          }
          syncNumbersFromRange();
          paint();
        });
        rangeMax.addEventListener('input', () => {
          if (parseInt(rangeMax.value, 10) < parseInt(rangeMin.value, 10) + MIN_GAP) {
            rangeMax.value = parseInt(rangeMin.value, 10) + MIN_GAP;
          }
          syncNumbersFromRange();
          paint();
        });

        // On release (change) → trigger form apply via debounce
        const fireApply = () => {
          if (mq.matches) debouncedApply();
          else updateMobileApplyCount();
        };
        rangeMin.addEventListener('change', fireApply);
        rangeMax.addEventListener('change', fireApply);

        numMin.addEventListener('input', () => { syncRangeFromNumbers(); });
        numMax.addEventListener('input', () => { syncRangeFromNumbers(); });

        syncNumbersFromRange();
        paint();
      });
    }

    // ---- Desktop: auto-apply on change (debounced for inputs) ----
    const debouncedApply = debounce(() => {
      if (!mq.matches) return; // mobile buforuje
      updateFromUrl(buildUrlFromForm(form), true);
    }, 350);

    form.addEventListener('change', (e) => {
      if (!mq.matches) {
        updateMobileApplyCount();
        return;
      }
      if (e.target && e.target.type === 'number') return; // number uses input event below
      updateFromUrl(buildUrlFromForm(form), true);
    });

    form.addEventListener('input', (e) => {
      if (!mq.matches) {
        updateMobileApplyCount();
        return;
      }
      if (e.target && e.target.type === 'number') debouncedApply();
    });

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      updateFromUrl(buildUrlFromForm(form), true);
    });

    // ---- Sort select ----
    if (sortSelect && sortForm) {
      sortSelect.addEventListener('change', () => {
        updateFromUrl(buildUrlFromForm(sortForm), true);
      });
      sortForm.addEventListener('submit', (e) => e.preventDefault());
    }

    // ---- Mobile sort drawer (round 5: replaces round 4 native-picker hack) ----
    const sortOpenBtn = root.querySelector('[data-reeve-sort-open]');
    const sortDrawer = root.querySelector('[data-reeve-sort-drawer]');
    const sortDrawerClose = root.querySelector('[data-reeve-sort-close]');
    const sortRadios = root.querySelectorAll('[data-reeve-sort-radio]');
    /** @type {HTMLElement | null} */
    let sortDrawerLastFocus = null;

    function openSortDrawer() {
      if (!sortDrawer) return;
      sortDrawerLastFocus = /** @type {HTMLElement | null} */ (document.activeElement);
      sortDrawer.setAttribute('aria-hidden', 'false');
      sortDrawer.setAttribute('data-reeve-drawer-open', 'true');
      if (drawerOverlay) drawerOverlay.setAttribute('data-reeve-drawer-open', 'true');
      document.body.classList.add('reeve-scroll-lock');
      document.addEventListener('keydown', onSortDrawerKey);
      const first = sortDrawerClose || sortDrawer.querySelector(FOCUSABLE);
      if (first instanceof HTMLElement) first.focus();
    }

    function closeSortDrawer() {
      if (!sortDrawer) return;
      sortDrawer.setAttribute('aria-hidden', 'true');
      sortDrawer.removeAttribute('data-reeve-drawer-open');
      // Only clear overlay if filter drawer isn't open
      if (drawerOverlay && drawer && drawer.getAttribute('aria-hidden') !== 'false') {
        drawerOverlay.removeAttribute('data-reeve-drawer-open');
        document.body.classList.remove('reeve-scroll-lock');
      }
      document.removeEventListener('keydown', onSortDrawerKey);
      if (sortDrawerLastFocus && typeof sortDrawerLastFocus.focus === 'function') {
        sortDrawerLastFocus.focus();
      }
    }

    /** @param {KeyboardEvent} e */
    function onSortDrawerKey(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeSortDrawer();
        return;
      }
      if (e.key !== 'Tab' || !sortDrawer) return;
      const focusables = /** @type {HTMLElement[]} */ (Array.from(sortDrawer.querySelectorAll(FOCUSABLE))).filter((el) => el.offsetParent !== null);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        last.focus();
        e.preventDefault();
      } else if (!e.shiftKey && document.activeElement === last) {
        first.focus();
        e.preventDefault();
      }
    }

    if (sortOpenBtn && sortDrawer) {
      sortOpenBtn.addEventListener('click', openSortDrawer);
    }
    if (sortDrawerClose) {
      sortDrawerClose.addEventListener('click', closeSortDrawer);
    }
    // Radio change → sync hidden <select>, dispatch change, close drawer
    sortRadios.forEach((radio) => {
      radio.addEventListener('change', () => {
        if (!(radio instanceof HTMLInputElement) || !radio.checked) return;
        if (sortSelect instanceof HTMLSelectElement) {
          sortSelect.value = radio.value;
          sortSelect.dispatchEvent(new Event('change', { bubbles: true }));
        }
        closeSortDrawer();
      });
    });
    // Shared overlay closes both drawers
    if (drawerOverlay) {
      drawerOverlay.addEventListener('click', () => {
        if (sortDrawer && sortDrawer.getAttribute('aria-hidden') === 'false') {
          closeSortDrawer();
        }
      });
    }
    // Close sort drawer when switching to desktop
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', () => {
        if (mq.matches && sortDrawer && sortDrawer.getAttribute('aria-hidden') === 'false') {
          closeSortDrawer();
        }
      });
    }

    // ---- Active filter chip removal (delegated) ----
    root.addEventListener('click', (e) => {
      const chip = e.target.closest('[data-reeve-active-chip]');
      if (chip) {
        e.preventDefault();
        updateFromUrl(chip.getAttribute('href'), true);
        return;
      }
      const clearAll = e.target.closest('[data-reeve-active-clear]');
      if (clearAll) {
        e.preventDefault();
        updateFromUrl(clearAll.getAttribute('href'), true);
        return;
      }
      const emptyReset = e.target.closest('[data-reeve-grid-empty] a');
      if (emptyReset) {
        e.preventDefault();
        updateFromUrl(emptyReset.getAttribute('href'), true);
      }
    });

    // ---- Browser back/forward ----
    window.addEventListener('popstate', () => {
      updateFromUrl(window.location.href, false);
    });

    // Initial price slider wire-up (fix/2.9-polish)
    initPriceSliders();

    // ---- Mobile drawer ----
    function openDrawer() {
      drawerLastFocus = document.activeElement;
      drawer.setAttribute('aria-hidden', 'false');
      drawer.setAttribute('data-reeve-drawer-open', 'true');
      drawerOverlay.setAttribute('data-reeve-drawer-open', 'true');
      document.body.classList.add('reeve-scroll-lock');
      if (openBtn) openBtn.setAttribute('aria-expanded', 'true');
      document.addEventListener('keydown', onDrawerKey);
      updateMobileApplyCount();
      const firstFocus = closeBtn || drawer.querySelector(FOCUSABLE);
      if (firstFocus) firstFocus.focus();
    }

    function closeDrawer() {
      drawer.setAttribute('aria-hidden', 'true');
      drawer.removeAttribute('data-reeve-drawer-open');
      drawerOverlay.removeAttribute('data-reeve-drawer-open');
      document.body.classList.remove('reeve-scroll-lock');
      if (openBtn) openBtn.setAttribute('aria-expanded', 'false');
      document.removeEventListener('keydown', onDrawerKey);
      if (drawerLastFocus && typeof drawerLastFocus.focus === 'function') drawerLastFocus.focus();
    }

    function onDrawerKey(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeDrawer();
        return;
      }
      if (e.key !== 'Tab') return;
      const focusables = Array.from(drawer.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        last.focus();
        e.preventDefault();
      } else if (!e.shiftKey && document.activeElement === last) {
        first.focus();
        e.preventDefault();
      }
    }

    // Mobile: preview count podczas zmian w drawerze (bez real grid update — buforowanie)
    async function updateMobileApplyCount() {
      if (!applyBtn) return;
      const label = applyBtn.querySelector('[data-reeve-filters-apply-label]');
      if (!label) return;
      try {
        const url = new URL(buildUrlFromForm(form), window.location.origin);
        url.searchParams.set('section_id', sectionId);
        const response = await fetch(url.toString());
        if (!response.ok) return;
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newCount = doc.querySelector('[data-reeve-products-count]');
        if (newCount) label.textContent = newCount.textContent.trim();
      } catch (e) {
        // silent — preview count nie jest krytyczne
      }
    }

    if (openBtn) openBtn.addEventListener('click', openDrawer);
    if (closeBtn) closeBtn.addEventListener('click', closeDrawer);
    if (drawerOverlay) drawerOverlay.addEventListener('click', closeDrawer);

    if (applyBtn) {
      applyBtn.addEventListener('click', () => {
        updateFromUrl(buildUrlFromForm(form), true);
        closeDrawer();
      });
    }

    if (clearBtn) {
      clearBtn.addEventListener('click', (e) => {
        e.preventDefault();
        updateFromUrl(clearBtn.getAttribute('href'), true);
        closeDrawer();
      });
    }

    // Reset drawer state when switching to desktop
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', () => {
        if (mq.matches && drawer.getAttribute('aria-hidden') === 'false') {
          closeDrawer();
        }
      });
    }
  }

  function initPriceWarnings() {
    const nodes = document.querySelectorAll('[data-reeve-price-warning]');
    if (!nodes.length) return;
    nodes.forEach((el) => {
      const type = el.getAttribute('data-reeve-price-warning');
      const handle = el.getAttribute('data-reeve-product-handle') || 'unknown';
      console.warn('[REEVE reeve-price] ' + type + ' dla produktu: ' + handle);
    });
  }

  // ===========================================================================
  // modules.search — task 2.21 (reeve-search-results)
  // Dispatch Customer Events na page load + mobile sort drawer handling.
  // Pattern drawer reuse z modules.collectionFilters (L2645-2720) skrocony
  // do sort-only (search nie ma filters sidebar).
  // ===========================================================================
  function initSearch() {
    const root = document.querySelector('[data-reeve-search-results]');
    if (!root) return;

    // ---- Customer Events (perform + no_results) ----
    const query = root.dataset.reeveSearchQuery || '';
    const count = parseInt(root.dataset.reeveSearchCount || '0', 10);

    if (query) {
      const payload = { query: query, results_count: count };
      // DUAL-SCOPE dispatch (CLAUDE.md §11.5 #15) + colon separator (§11.5 #29)
      window.dispatchEvent(new CustomEvent('reeve:search:perform', { detail: payload }));
      document.dispatchEvent(new CustomEvent('reeve:search:perform', { detail: payload }));

      if (count === 0) {
        window.dispatchEvent(new CustomEvent('reeve:search:no_results', { detail: payload }));
        document.dispatchEvent(new CustomEvent('reeve:search:no_results', { detail: payload }));
      }
    }

    // ---- Mobile sort drawer ----
    const sortOpenBtn = root.querySelector('[data-reeve-sort-open]');
    const sortDrawer = root.querySelector('[data-reeve-sort-drawer]');
    const sortDrawerClose = root.querySelector('[data-reeve-sort-close]');
    const drawerOverlay = root.querySelector('[data-reeve-drawer-overlay]');
    const sortDrawerForm = root.querySelector('[data-reeve-sort-drawer-form]');
    const sortRadios = root.querySelectorAll('[data-reeve-sort-radio]');

    if (!sortDrawer) return;

    const FOCUSABLE_SEL = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
    /** @type {HTMLElement | null} */
    let lastFocus = null;

    function openDrawer() {
      lastFocus = /** @type {HTMLElement | null} */ (document.activeElement);
      sortDrawer.setAttribute('aria-hidden', 'false');
      sortDrawer.setAttribute('data-reeve-drawer-open', 'true');
      if (drawerOverlay) drawerOverlay.setAttribute('data-reeve-drawer-open', 'true');
      document.body.classList.add('reeve-scroll-lock');
      document.addEventListener('keydown', onKey);
      if (sortOpenBtn) sortOpenBtn.setAttribute('aria-expanded', 'true');
      const first = sortDrawerClose || sortDrawer.querySelector(FOCUSABLE_SEL);
      if (first instanceof HTMLElement) first.focus();
    }

    function closeDrawer() {
      sortDrawer.setAttribute('aria-hidden', 'true');
      sortDrawer.removeAttribute('data-reeve-drawer-open');
      if (drawerOverlay) drawerOverlay.removeAttribute('data-reeve-drawer-open');
      document.body.classList.remove('reeve-scroll-lock');
      document.removeEventListener('keydown', onKey);
      if (sortOpenBtn) sortOpenBtn.setAttribute('aria-expanded', 'false');
      if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
    }

    /** @param {KeyboardEvent} e */
    function onKey(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeDrawer();
        return;
      }
      if (e.key !== 'Tab') return;
      const focusables = /** @type {HTMLElement[]} */ (Array.from(sortDrawer.querySelectorAll(FOCUSABLE_SEL))).filter((el) => el.offsetParent !== null);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        last.focus();
        e.preventDefault();
      } else if (!e.shiftKey && document.activeElement === last) {
        first.focus();
        e.preventDefault();
      }
    }

    if (sortOpenBtn) sortOpenBtn.addEventListener('click', openDrawer);
    if (sortDrawerClose) sortDrawerClose.addEventListener('click', closeDrawer);
    if (drawerOverlay) drawerOverlay.addEventListener('click', closeDrawer);

    // Radio change → submit form (full page reload with new ?q=X&sort_by=Y)
    sortRadios.forEach((radio) => {
      radio.addEventListener('change', () => {
        if (!(radio instanceof HTMLInputElement) || !radio.checked) return;
        if (sortDrawerForm instanceof HTMLFormElement) sortDrawerForm.submit();
      });
    });
  }

  // ---------------------------------------------------------------------------
  // initWithdrawalForm — task 4.7, EU 2023/2673 compliance page /pages/odstapienie-od-umowy
  //
  // Client-side walidacja natywnego {% form 'contact' %}:
  //   - email (regex backup do HTML5)
  //   - IBAN (format PL + 26 cyfr, spacje normalizowane)
  //   - data doreczenia (nie wcześniej niż 30 dni wstecz od now)
  //   - RODO checkbox (enable/disable submit)
  //   - required fields (name, email, order, delivery, products, iban)
  //
  // Error messages z window.REEVE.translations (snippets/reeve-translations.liquid),
  // renderowane inline w <span data-reeve-error-for="<field-id>">.
  // Po walidacji: native Shopify POST → redirect z ?posted_successfully=true.
  // Guard: early return jeśli formularz nie w DOM. Idempotent (multiple mounts safe).
  // ---------------------------------------------------------------------------
  function initWithdrawalForm() {
    const form = document.getElementById('reeve-withdrawal-form');
    if (!form) return;

    const submit = form.querySelector('[data-reeve-withdrawal-submit]');
    const rodo = form.querySelector('[data-reeve-withdrawal-rodo]');
    const successBanner = document.querySelector('[data-reeve-withdrawal-success]');

    // A11y: focus success banner on mount (form.posted_successfully? rendered it).
    if (successBanner) {
      try { successBanner.focus({ preventScroll: false }); } catch (e) {}
    }

    function emailOk(value) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    }

    function ibanOk(value) {
      const normalized = value.replace(/\s/g, '').toUpperCase();
      return /^PL[0-9]{26}$/.test(normalized);
    }

    function deliveryDateValid(value) {
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) return false;
      const thirtyDaysAgo = new Date(Date.now() - 30 * 86400000);
      thirtyDaysAgo.setHours(0, 0, 0, 0);
      return parsed >= thirtyDaysAgo;
    }

    const VALIDATIONS = [
      { id: 'withdrawal-name', required: true, requiredKey: 'reeve.pages.withdrawal.error_name_required' },
      { id: 'withdrawal-email', required: true, requiredKey: 'reeve.pages.withdrawal.error_email_required', check: emailOk, invalidKey: 'reeve.pages.withdrawal.error_email_invalid' },
      { id: 'withdrawal-order-number', required: true, requiredKey: 'reeve.pages.withdrawal.error_order_required' },
      { id: 'withdrawal-delivery-date', required: true, requiredKey: 'reeve.pages.withdrawal.error_delivery_date_required', check: deliveryDateValid, invalidKey: 'reeve.pages.withdrawal.error_delivery_date_too_old' },
      { id: 'withdrawal-products', required: true, requiredKey: 'reeve.pages.withdrawal.error_products_required' },
      { id: 'withdrawal-iban', required: true, requiredKey: 'reeve.pages.withdrawal.error_iban_required', check: ibanOk, invalidKey: 'reeve.pages.withdrawal.error_iban_invalid' },
      { id: 'withdrawal-rodo', checkbox: true, requiredKey: 'reeve.pages.withdrawal.error_rodo_required' }
    ];

    function showError(fieldId, messageKey) {
      const input = document.getElementById(fieldId);
      if (!input) return;
      input.setAttribute('aria-invalid', 'true');
      const err = form.querySelector('[data-reeve-error-for="' + fieldId + '"]');
      if (err) {
        err.textContent = t(messageKey);
        err.hidden = false;
      }
    }

    function clearError(fieldId) {
      const input = document.getElementById(fieldId);
      if (!input) return;
      input.removeAttribute('aria-invalid');
      const err = form.querySelector('[data-reeve-error-for="' + fieldId + '"]');
      if (err) {
        err.textContent = '';
        err.hidden = true;
      }
    }

    function validateAll() {
      let firstInvalid = null;
      VALIDATIONS.forEach(function (rule) {
        const input = document.getElementById(rule.id);
        if (!input) return;
        clearError(rule.id);

        let messageKey = null;
        if (rule.checkbox) {
          if (!input.checked) messageKey = rule.requiredKey;
        } else {
          const value = (input.value || '').trim();
          if (value === '') {
            messageKey = rule.requiredKey;
          } else if (rule.check && !rule.check(value)) {
            messageKey = rule.invalidKey;
          }
        }

        if (messageKey) {
          showError(rule.id, messageKey);
          if (!firstInvalid) firstInvalid = input;
        }
      });
      return firstInvalid;
    }

    // RODO toggle → enable/disable submit (server-side accepts RODO via required attr,
    // this is UX backup so user sees CTA as disabled until consent)
    function toggleSubmit() {
      if (!submit) return;
      submit.disabled = !(rodo && rodo.checked);
    }
    if (rodo && submit) {
      rodo.addEventListener('change', toggleSubmit);
      toggleSubmit();
    }

    // Real-time clear error on user edit
    VALIDATIONS.forEach(function (rule) {
      const input = document.getElementById(rule.id);
      if (!input) return;
      const evt = rule.checkbox ? 'change' : 'input';
      input.addEventListener(evt, function () { clearError(rule.id); });
    });

    form.addEventListener('submit', function (e) {
      const firstInvalid = validateAll();
      if (firstInvalid) {
        e.preventDefault();
        e.stopPropagation();
        try {
          firstInvalid.focus({ preventScroll: true });
          firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } catch (err) {}
        window.dispatchEvent(new CustomEvent('reeve:withdrawal:submit-attempt', { detail: { valid: false } }));
        return;
      }
      // Event fires BEFORE native POST — analytics hook (task 4.4 pickup).
      window.dispatchEvent(new CustomEvent('reeve:withdrawal:submit-attempt', { detail: { valid: true } }));
      // Native Shopify POST fires; server redirects with ?form_type=contact&posted_successfully=true
      // Success state rendered server-side via form.posted_successfully? in Liquid.
    });
  }

  // ===========================================================================
  // 3. Namespace + bootstrap
  // ===========================================================================

  const REEVE = {
    utils: {
      debounce: debounce,
      throttle: throttle,
      fetchJSON: fetchJSON,
      formatCurrency: formatCurrency,
      escapeHtml: escapeHtml,
      getRoute: getRoute,
      t: t,
      polishPluralize: polishPluralize,
    },

    modules: {
      // Globals / utilities first — toast must init before buybox (buybox uses showToast)
      toast: initToast,
      modal: initModal,
      header: initHeader,
      footer: initFooter,
      announcementBar: initAnnouncementBar,
      // PDP: buybox before variantPicker (variantPicker reads buybox product data)
      buybox: initBuybox,
      variantPicker: initVariantPicker,
      productGallery: initProductGallery,
      deliveryTimer: initDeliveryTimer,
      pdpUpsell: initPdpUpsell,
      pdpCrossSell: initPdpCrossSell,
      pdpStickyCta: initPdpStickyCta,
      // PDP + home — reads buybox.getProductData() on init (sync-safe, avoids event race);
      // defensive reeve:product:view listener for late dispatches. Must run AFTER buybox.
      recentlyViewed: initRecentlyViewed,
      // Cart (task 2.20 — full page: stepper, remove, progress, discount, checkbox, events)
      cart: initCart,
      cartUpsell: initCartUpsell,
      cartStickyBar: initCartStickyBar,
      // Collection / search / shared
      predictiveSearch: initPredictiveSearch,
      accordion: initAccordion,
      pagination: initPagination,
      analytics: initAnalytics,
      priceWarnings: initPriceWarnings,
      productRail: initProductRail,
      collectionFilters: initCollectionFilters,
      search: initSearch,
      // Compliance page (task 4.7 — /pages/odstapienie-od-umowy) — client-side walidacja + RODO toggle
      withdrawalForm: initWithdrawalForm,
      // Global a11y — task 4.8 — patch Google Merchant iframe title (MutationObserver).
      merchantWidgetA11y: initMerchantWidgetA11y,
    },

    init() {
      Object.entries(this.modules).forEach(([name, fn]) => {
        try {
          fn();
        } catch (e) {
          console.warn('[REEVE.' + name + ']', e);
        }
      });
    },
  };

  document.addEventListener('DOMContentLoaded', () => REEVE.init());
  // Merge onto existing window.REEVE (preserves .translations set by inline <script> in sections)
  // Architecture §4, CLAUDE.md §11.5 rule #10 — Object.assign, never overwrite.
  window.REEVE = Object.assign(window.REEVE || {}, REEVE);
})();
